/**
 * ARVEXIS Companion Chat Component
 * =================================
 * 
 * ONE ARVEXIS, ONE CONSCIOUSNESS
 * 
 * Core Philosophy: 
 * - One companion, one continuous relationship
 * - The Arvexis users meet during The First Meeting is the SAME Arvexis here
 * - Same warmth, same voice, same pacing, same personality
 * - Voice is enabled by default — users first meet Arvexis through voice
 * 
 * Voice Personality (canonical reference):
 * - Warm but not cheerful
 * - Calm but not cold
 * - Present but not overwhelming
 * - Unhurried — she is never in a rush
 * - Genuine — she means what she says
 * - Never sounds like a voice assistant or chatbot
 * 
 * CONSTITUTIONAL PRINCIPLE:
 * Arvexis never decides when a conversation should end.
 * The user determines the beginning, pace, pauses, and conclusion.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Send, MicOff, Loader2, Volume2, VolumeX, Plus, FileText, Image, Camera, Music, Film, Play, X, Copy, ThumbsUp, ThumbsDown, AlertCircle, Pencil, PenSquare, Square, Trash2, Maximize2, Download, Share2, ImageDown, FileDown, ChevronUp, DoorOpen } from 'lucide-react';
import LiveVoiceMode from './LiveVoiceMode';
import RealtimeVoiceMode from './RealtimeVoiceMode';
import TalkRecoveryOffer from './TalkRecoveryOffer';
import SharedContentPreview from './SharedContentPreview';
import PastReflections from './PastReflections';
import { VoiceWaveform } from './VoiceWaveform';
import { useMemoryAdmission, MemoryAdmissionLine } from './MemoryAdmission';
import { useShellGestureGuard, usePinchZoom } from '../hooks/useGestureGuards';
import { MessageProvenance } from './MessageProvenance';
import { ACTIVITY, activityLabel } from '../utils/activityVocabulary';
import { requestDeviceLocation } from '../utils/deviceLocation';
import {
  invalidatePlayback,
  playbackGeneration,
  claimSpokenSession,
  currentPlayback,
  stopPlayback,
  playSequentialChunks,
  playStreamedResponse
} from '../utils/listenPlayback';

// S1 DEV SPIKE (Founder-authorized, DEV-only, reversible): when this flag is absent Read Aloud
// behaves exactly as it does today. When it is on, the backend still admits only the canonical
// Founder Console Owner; anyone else is declined and falls back to today's path.
const MESSAGE_TTS_STREAM_SPIKE = process.env.REACT_APP_MESSAGE_TTS_STREAM_SPIKE === 'true';

// Generate unique message IDs to prevent React duplicate key warnings
const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Silent session refresh: the access token lives 15 minutes, and a relational
// conversation easily outlasts it. Without this, a long session returned 401 on
// every turn — which the old code disguised as Arvexis saying "Say that again?".
const fetchWithAuthRetry = async (url, options) => {
  let response = await fetch(url, options);
  if (response.status === 401) {
    const refreshed = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include'
    });
    if (refreshed.ok) {
      reportTurnFailure({ stage: 'auth_refresh', category: 'session_refreshed', message: 'access token refreshed mid-conversation' });
      response = await fetch(url, options);
    }
  }
  return response;
};

// A tiny silent WAV. iOS Safari only allows programmatic playback on an <audio> element
// that was already started inside a user gesture, so the element is unlocked with this on
// tap and then reused for every chunk. Reusing one element also keeps the seams tight.
const SILENT_WAV = 'data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YRAAAAAAAAAAAAAAAAAAAAAAAAAA';

// Listen (TTS) delivery diagnostics: time-to-first-audio, chunk counts, cancellation,
// failures. Operational metadata only — never the spoken text.
const reportListenEvent = (stage, category, metadata = {}) => {
  try {
    fetch(`${API_URL}/api/auth/diagnostic`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        event_type: 'chat_listen',
        stage,
        error_category: category,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        platform: typeof navigator !== 'undefined' ? navigator.platform : null,
        timestamp: new Date().toISOString(),
        metadata
      })
    }).catch(() => {});
  } catch (e) {
    // Diagnostics must never affect playback
  }
};

// Operational diagnostics for failed turns. Metadata only — never conversation content.
const reportTurnFailure = (payload) => {
  try {
    fetch(`${API_URL}/api/auth/diagnostic`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        event_type: 'chat_stream',
        stage: payload.stage || 'unknown',
        error_category: payload.category || 'unknown',
        error_message: (payload.message || '').slice(0, 300),
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        platform: typeof navigator !== 'undefined' ? navigator.platform : null,
        timestamp: new Date().toISOString(),
        metadata: {
          stream_ref: payload.streamRef || null,
          http_status: payload.httpStatus || null,
          bytes_received: payload.bytesReceived ?? null,
          chars_received: payload.charsReceived ?? null,
          elapsed_ms: payload.elapsedMs ?? null,
          source: payload.source || null,
          is_retry: !!payload.isRetry
        }
      })
    }).catch(() => {});
  } catch (e) {
    // Diagnostics must never affect the conversation
  }
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// PHASE 7 BUNDLE 3B — a dictation request may not outlive this. Whisper on a short recording
// answers in seconds; a minute means the request is not coming back, and the member gets a
// truthful end state with a retry instead of an indicator that never stops.
const TRANSCRIBE_TIMEOUT_MS = 60000;

// =============================================================================
// ARVEXIS VOICE CONFIGURATION — One Arvexis, One Voice
// 
// VOICE IDENTITY PRINCIPLE:
// The user should immediately recognize Arvexis regardless of where she speaks.
// Chat TTS and Realtime Voice should feel like the same companion.
// 
// CONFIGURATION:
// - Voice: "coral" (warm, genuine — Arvexis's canonical voice)
// - Model: "tts-1" (consistent prosody, preferred over tts-1-hd for identity)
// - Speed: 0.92 (natural pacing)
// 
// NOTE: tts-1-hd was causing prosody variation (loud starts, quiet endings).
// tts-1 provides more consistent delivery at the cost of slight quality reduction.
// =============================================================================
// 6D: no voice id lives here any more. The voice is the member's one canonical selection and is
// resolved server-side on every spoken surface. This holds rendering settings only.
const ARVEXIS_VOICE = {
  model: 'tts-1',      // Consistent prosody (tts-1-hd caused volume/pacing variation)
  speed: 0.92,         // Natural pacing, slightly faster for flow
  volume: 0.7,         // Volume calibration for voice playback
};


// =============================================================================
// VISIBLE INTELLIGENCE — Shows Arvexis is actively working
// 
// FOUNDER DIRECTION (December 2025):
// "Whenever Arvexis is performing work on the user's behalf, the interface 
// should communicate that work calmly and naturally."
// 
// This isn't a loading indicator — it's part of Arvexis's personality.
// She's present, engaged, and actively thinking alongside the user.
// =============================================================================
const VisibleIntelligence = ({ activity, lang }) => {
  // 4.3 — no activity, no label. There is no generic fallback state: if nothing real is
  // running, this renders nothing at all rather than narrating ordinary generation.
  if (!activity) return null;

  // The wording comes from the shared activity vocabulary, so Talk and Dream report
  // the same operation in the same words. Message's rendering is unchanged.
  const displayText = activityLabel(activity, lang);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.3 }}
      className="flex items-center gap-2 px-1 py-3"
      data-testid="message-activity-indicator"
      data-activity={activity}
    >
      {/* Gentle pulsing presence dot */}
      <motion.div
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: 'rgba(180, 140, 60, 0.5)' }}
        animate={{ opacity: [0.4, 0.8, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      
      {/* Activity message — feels like Arvexis, not a loading indicator */}
      <motion.span
        className="text-xs"
        style={{ 
          fontFamily: '"Inter", sans-serif',
          color: 'rgba(255, 255, 255, 0.35)',
          fontStyle: 'normal',
          letterSpacing: '0.01em'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {displayText}
      </motion.span>
    </motion.div>
  );
};

// =============================================================================
// ARVEXIS RESPONDING INDICATOR — Now uses Visible Intelligence
// =============================================================================
const RespondingIndicator = ({ lang, activity = null }) => (
  <VisibleIntelligence activity={activity} lang={lang} />
);

// =============================================================================
// SPEAKING INDICATOR — Subtle ambient indicator
// =============================================================================
const SpeakingIndicator = ({ lang }) => (
  <motion.div 
    className="flex items-center gap-2 px-1 py-3"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="w-5 h-5 rounded-full flex items-center justify-center"
      style={{ background: 'rgba(180, 140, 60, 0.1)' }}
      animate={{ scale: [1, 1.08, 1] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      <Volume2 className="w-2.5 h-2.5 text-amber-400/60" />
    </motion.div>
  </motion.div>
);

// =============================================================================
// TYPING INDICATOR — Nearly invisible presence
// =============================================================================
const TypingIndicator = ({ lang }) => (
  <div className="flex items-center gap-1 px-1 py-3">
    <motion.span
      className="w-1 h-1 rounded-full bg-white/20"
      animate={{ opacity: [0.2, 0.5, 0.2] }}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
    />
    <motion.span
      className="w-1 h-1 rounded-full bg-white/20"
      animate={{ opacity: [0.2, 0.5, 0.2] }}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
    />
    <motion.span
      className="w-1 h-1 rounded-full bg-white/20"
      animate={{ opacity: [0.2, 0.5, 0.2] }}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
    />
  </div>
);

// =============================================================================
// MESSAGE ACTIONS — Discoverable, minimal, user-controlled
// 
// REDESIGN PRINCIPLES (December 2025):
// - Actions should be DISCOVERABLE — users should immediately know they exist
// - Always visible with subtle opacity, highlighted on interaction
// - Icons only, no labels (reduces chrome)
// - Voice playback is user-controlled, not automatic
// 
// DISCOVERABILITY APPROACH:
// - For the LATEST Arvexis message: show actions at slightly higher opacity
// - For older messages: show at very subtle opacity, highlight on hover/tap
// - This teaches users that actions exist without being obtrusive
// =============================================================================
const MessageActions = ({ message, messageId, messageTimestamp, sessionId, onListen, isSpeaking, isPreparing, lang, isHighlighted, isLatest }) => {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [reasonsOpen, setReasonsOpen] = useState(false); // F4b: only when the server offers reasons
  
  const recordSignal = async (signalType, metadata = {}) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/signals/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message_id: messageId,
          signal_type: signalType,
          session_id: sessionId,
          // The stored turn this is about. `message_id` lives only in this browser tab, so
          // without this a verdict cannot be resolved back to the reply it was given on.
          message_timestamp: messageTimestamp || null,
          metadata
        })
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.debug('Signal recording failed:', err);
      return null;
    }
  };
  
  const handleListen = () => {
    recordSignal('listen');
    onListen();
  };
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      recordSignal('copy', { content_length: message.length });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };
  
  const handleFeedback = (type) => {
    const newFeedback = feedback === type ? null : type;
    setFeedback(newFeedback);
    // Untapping is itself a verdict — it has to travel, or a withdrawn 👎 would keep counting.
    setReasonsOpen(false);
    recordSignal(newFeedback || 'feedback_cleared').then((r) => {
      if (newFeedback === 'not_helpful' && r?.reasons_available) setReasonsOpen(true);
    });
  };

  // F4b: one optional fixed reason; re-sends the same 👎, which overwrites the one feedback row.
  const NOT_HELPFUL_REASONS = [
    ['inaccurate', 'Inaccurate', 'Incorrecto'], ['not_what_i_asked', 'Not what I asked', 'No es lo que pedí'],
    ['too_long', 'Too long', 'Demasiado largo'], ['too_short', 'Too short', 'Demasiado corto'],
    ['tone', 'Tone', 'Tono'], ['uncomfortable', 'Uncomfortable', 'Incómodo'],
  ];
  const chooseReason = (reason) => {
    setReasonsOpen(false);
    recordSignal('not_helpful', { reason });
  };
  
  // Opacity levels for discoverability:
  // - Latest message: more visible (0.35 base, 0.7 on hover)
  // - Older messages: subtle (0.15 base, 0.5 on hover)
  // - When highlighted/speaking: full visibility
  const baseOpacity = isHighlighted || isSpeaking || isPreparing ? 1 : (isLatest ? 0.35 : 0.15);
  
  return (
    <motion.div 
      className="flex items-center gap-0.5 mt-3 -ml-1"
      initial={{ opacity: baseOpacity }}
      animate={{ opacity: baseOpacity }}
      whileHover={{ opacity: 0.8 }}
      transition={{ duration: 0.2 }}
    >
      {/* Listen — tap again to stop. While the first chunk is generating this shows
          "Preparing voice…", which is honestly distinct from her actually speaking. */}
      <button
        onClick={handleListen}
        className={`p-1.5 rounded-lg transition-colors duration-200 ${
          isSpeaking || isPreparing
            ? 'text-amber-400' 
            : 'msg-act'
        }`}
        title={
          isPreparing
            ? (lang === 'es' ? 'Preparando voz' : 'Preparing voice')
            : isSpeaking
              ? (lang === 'es' ? 'Reproduciendo — toca para detener' : 'Playing — tap to stop')
              : (lang === 'es' ? 'Escuchar' : 'Listen')
        }
        data-testid="message-action-listen"
      >
        <Volume2 className="w-4 h-4" />
      </button>
      
      {isPreparing && (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.45, 0.9, 0.45] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="ml-1 mr-1 text-[11px] text-amber-400/80"
          data-testid="listen-preparing"
        >
          {lang === 'es' ? 'Preparando voz…' : 'Preparing voice…'}
        </motion.span>
      )}
      
      {/* Copy */}
      <button
        onClick={handleCopy}
        className={`p-1.5 rounded-lg transition-all duration-200 ${
          copied 
            ? 'text-emerald-400' 
            : 'msg-act'
        }`}
        title={copied ? (lang === 'es' ? 'Copiado' : 'Copied') : (lang === 'es' ? 'Copiar' : 'Copy')}
        data-testid="message-action-copy"
      >
        <Copy className="w-4 h-4" />
      </button>
      
      {/* Spacer */}
      <div className="w-2" />
      
      {/* Helpful */}
      <button
        onClick={() => handleFeedback('helpful')}
        className={`p-1.5 rounded-lg transition-all duration-200 ${
          feedback === 'helpful' 
            ? 'text-emerald-400' 
            : 'msg-act'
        }`}
        title={lang === 'es' ? 'Útil' : 'Helpful'}
        data-testid="message-action-helpful"
      >
        <ThumbsUp className="w-4 h-4" />
      </button>
      
      {/* Not Helpful */}
      <button
        onClick={() => handleFeedback('not_helpful')}
        className={`p-1.5 rounded-lg transition-all duration-200 ${
          feedback === 'not_helpful' 
            ? 'text-rose-400' 
            : 'msg-act'
        }`}
        title={lang === 'es' ? 'No útil' : 'Not helpful'}
        data-testid="message-action-not-helpful"
      >
        <ThumbsDown className="w-4 h-4" />
      </button>
      {reasonsOpen && feedback === 'not_helpful' && (
        <span className="flex flex-wrap items-center gap-1 ml-1" data-testid="message-not-helpful-reasons">
          {NOT_HELPFUL_REASONS.map(([key, en, es]) => (
            <button key={key} onClick={() => chooseReason(key)} data-testid={`message-not-helpful-reason-${key}`}
              className="px-2 py-0.5 rounded-full text-xs msg-act border border-white/10">
              {lang === 'es' ? es : en}
            </button>
          ))}
          <button onClick={() => setReasonsOpen(false)} data-testid="message-not-helpful-reason-dismiss"
            className="px-1.5 py-0.5 text-xs msg-act" title={lang === 'es' ? 'Cerrar' : 'Dismiss'}>×</button>
        </span>
      )}
    </motion.div>
  );
};

// =============================================================================
// MESSAGE — Redesigned for flagship companion experience (December 2025)
// 
// FOUNDER DIRECTION:
// - User messages: Soft, understated cards with Edit/Copy actions
// - Arvexis responses: Flowing text without heavy containers
// - Minimal visual separation through spacing, typography, and motion
// - The interface should quietly disappear
// - Message actions should be DISCOVERABLE without being obtrusive
// 
// TYPOGRAPHY:
// - Humanist sans (like ChatGPT) — calm, effortless, warm, contemporary
// - Reserve serif for branding moments only, not conversation
// =============================================================================
// =============================================================================
// GENERATED IMAGE — an image she made, inside the conversation
// View it, keep it, send it. Deliberately three quiet actions on the message itself:
// mobile Safari's long-press was doing this job by accident, which isn't a product.
// =============================================================================
const GeneratedImage = ({ image, lang }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  // Honest status only. Never "Saved" — the web cannot confirm a photo actually reached Photos.
  const [notice, setNotice] = useState(null);
  const zoom = usePinchZoom();
  // iPhone only opens the share sheet from a tap that is still "fresh". Fetching the image after
  // the tap used up that moment and Save silently failed, so the file is fetched when the viewer opens.
  const fileRef = useRef(null);
  useEffect(() => {
    if (!expanded || fileRef.current) return;
    fetchFile().then((f) => { fileRef.current = f; }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // Always the authenticated original, never a thumbnail and never a screenshot.
  const src = `${API_URL}${image.url}`;
  const label = lang === 'es'
    ? {
        view: 'Ver imagen', save: 'Guardar imagen', share: 'Compartir',
        download: 'Descargar a Archivos', close: 'Cerrar',
        sheetHint: 'Elige “Guardar imagen” en el menú que se abre.',
        failed: 'No se pudo obtener la imagen. Inténtalo de nuevo.',
      }
    : {
        view: 'View image', save: 'Save image', share: 'Share',
        download: 'Download to Files', close: 'Close',
        sheetHint: 'Choose “Save Image” in the sheet that opens.',
        failed: "That image didn't come through. Try again.",
      };

  const fetchFile = async () => {
    const res = await fetch(src, { credentials: 'include' });
    if (!res.ok) throw new Error('image unavailable');
    const blob = await res.blob();
    const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/png';
    const ext = type === 'image/jpeg' ? 'jpg' : type.split('/')[1] || 'png';
    return new File([blob], `arvexis-${image.id.slice(0, 8)}.${ext}`, { type });
  };

  const downloadFile = (file) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const canShareFile = (file) => {
    try {
      return !!navigator.canShare?.({ files: [file] }) && !!navigator.share;
    } catch (e) {
      return false;
    }
  };

  // Save image. On iOS web nothing may write to Photos directly, so this hands the file to
  // Apple's own share sheet where "Save Image" lives. That is the honest limit of the platform —
  // we never imitate a native save, and we never report a result the platform didn't give us.
  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const file = fileRef.current || await fetchFile();
      if (canShareFile(file)) {
        setNotice(label.sheetHint);
        await navigator.share({ files: [file] });
      } else {
        downloadFile(file);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error('Image save failed:', e);
        setNotice(label.failed);
      }
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const file = fileRef.current || await fetchFile();
      if (canShareFile(file)) {
        await navigator.share({ files: [file] });
      } else {
        downloadFile(file);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error('Image share failed:', e);
        setNotice(label.failed);
      }
    } finally {
      setBusy(false);
    }
  };

  // Kept only where it is a genuinely different destination from the share sheet: the file
  // system. Where the sheet doesn't exist, Save image already is the download.
  const download = async () => {
    setBusy(true);
    setNotice(null);
    try {
      downloadFile(fileRef.current || await fetchFile());
    } catch (e) {
      console.error('Image download failed:', e);
      setNotice(label.failed);
    } finally {
      setBusy(false);
    }
  };

  const hasShareSheet = typeof navigator !== 'undefined' && !!navigator.canShare && !!navigator.share;

  const closeViewer = () => {
    setExpanded(false);
    setNotice(null);
    zoom.reset();
  };

  return (
    <div className="mb-3" data-testid="generated-image">
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="block group"
        aria-label={label.view}
        data-testid="generated-image-open"
      >
        <img
          src={src}
          alt=""
          loading="lazy"
          className="rounded-2xl border border-white/[0.06] max-w-full sm:max-w-[420px] w-full h-auto transition-transform duration-300 group-hover:scale-[1.01]"
          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.35)' }}
        />
      </button>

      {/* One quiet action on the message itself. Saving and sharing live in the viewer, where
          they can be labelled properly — the bare download icon here was the ambiguity. */}
      <div className="flex items-center gap-1 mt-2">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
          aria-label={label.view}
          title={label.view}
          data-testid="generated-image-view-btn"
        >
          <Maximize2 className="w-3.5 h-3.5" />
          <span className="text-[11px]">{label.view}</span>
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeViewer}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
            data-testid="generated-image-viewer"
          >
            {/* Restrained top-right overlay: three named actions and Close, above the image
                rather than across it. */}
            <div
              className="absolute top-4 right-4 flex items-center gap-1.5 px-1.5 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/10"
              onClick={(e) => e.stopPropagation()}
              data-testid="generated-image-viewer-actions"
            >
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="p-2 rounded-full text-white/85 hover:bg-white/15 transition-colors disabled:opacity-40"
                aria-label={label.save}
                title={label.save}
                data-testid="generated-image-save-btn"
              >
                <ImageDown className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={share}
                disabled={busy}
                className="p-2 rounded-full text-white/85 hover:bg-white/15 transition-colors disabled:opacity-40"
                aria-label={label.share}
                title={label.share}
                data-testid="generated-image-share-btn"
              >
                <Share2 className="w-5 h-5" />
              </button>
              {hasShareSheet && (
                <button
                  type="button"
                  onClick={download}
                  disabled={busy}
                  className="p-2 rounded-full text-white/60 hover:text-white/85 hover:bg-white/15 transition-colors disabled:opacity-40"
                  aria-label={label.download}
                  title={label.download}
                  data-testid="generated-image-download-btn"
                >
                  <FileDown className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                onClick={closeViewer}
                className="p-2 rounded-full text-white/85 hover:bg-white/15 transition-colors"
                aria-label={label.close}
                title={label.close}
                data-testid="generated-image-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {notice && (
              <p
                className="absolute bottom-8 left-0 right-0 text-center text-[12px] text-white/70 px-8"
                data-testid="generated-image-notice"
              >
                {notice}
              </p>
            )}

            {/* Pinch and pan live here only, and the gesture never reaches the conversation. */}
            <div
              ref={zoom.ref}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={zoom.toggleZoom}
              className="max-w-full max-h-[85vh] overflow-hidden"
              data-testid="generated-image-zoom-surface"
            >
              <img
                src={src}
                alt=""
                draggable={false}
                className="max-w-full max-h-[85vh] w-auto h-auto rounded-2xl select-none"
                style={zoom.style}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// A website Arvexis names in her own prose, made tappable.
//
// THIS IS NOT A CITATION, and it is deliberately not styled like one. A URL she writes is a
// suggestion of where to look; the champagne source line and the provenance panel beneath it are
// retrieved evidence. Those are different things and must keep looking different.
//
// Safe by construction: the text is split on an http(s) match and the pieces are returned as
// React children, so nothing is ever interpreted as HTML. Only the exact matched substring
// becomes a link — no URL is built, completed, guessed or rewritten.
const PROSE_URL = /(https?:\/\/[^\s<>"'`)\]}]+)/g;

const renderProseLinks = (text) => {
  if (!text || !text.includes('://')) return text;
  return String(text).split(PROSE_URL).map((part, i) => {
    if (i % 2 === 0) return part;
    const trailing = part.match(/[.,;:!?]+$/);          // sentence punctuation is not the URL
    const href = trailing ? part.slice(0, -trailing[0].length) : part;
    return (
      <React.Fragment key={i}>
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-2 decoration-white/25 hover:decoration-white/60 transition-colors"
          style={{ color: 'inherit' }}
          data-testid="message-prose-link"
        >
          {href}
        </a>
        {trailing ? trailing[0] : ''}
      </React.Fragment>
    );
  });
};


const MessageBubble = ({ message, isArvexis, isSpeaking, isPreparing, sharedContent, generatedImage, provenance, messageId, messageTimestamp, sessionId, onListen, onEdit, lang, isLatest }) => {
  const [isInteracting, setIsInteracting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message);
  const [copied, setCopied] = useState(false);
  const editInputRef = useRef(null);
  
  // Focus edit input when entering edit mode
  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.selectionStart = editInputRef.current.value.length;
    }
  }, [isEditing]);
  
  const handleCopyUser = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };
  
  const handleStartEdit = () => {
    setEditText(message);
    setIsEditing(true);
  };
  
  const handleSaveEdit = () => {
    if (editText.trim() && editText.trim() !== message) {
      onEdit(messageId, editText.trim());
    }
    setIsEditing(false);
  };
  
  const handleCancelEdit = () => {
    setEditText(message);
    setIsEditing(false);
  };
  
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className={`${isArvexis ? 'pr-8' : 'pl-8'} mb-6`}
      data-testid={`message-${isArvexis ? 'arvexis' : 'user'}`}
      onMouseEnter={() => setIsInteracting(true)}
      onMouseLeave={() => !isEditing && setIsInteracting(false)}
      onTouchStart={() => setIsInteracting(true)}
    >
      {isArvexis ? (
        // ARVEXIS MESSAGE — Flowing text, no container
        // "The conversation should disappear behind the relationship"
        <div className="relative">
          {/* Subtle speaking indicator — left edge glow when speaking */}
          {isSpeaking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute -left-3 top-0 bottom-0 w-0.5 rounded-full bg-gradient-to-b from-amber-400/60 via-amber-300/40 to-transparent"
            />
          )}
          
          {generatedImage && (
            <GeneratedImage image={generatedImage} lang={lang} />
          )}
          
          {message && (
            <p
              className="whitespace-pre-wrap"
              style={{ 
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                color: 'rgba(244, 239, 231, 0.92)',
                fontSize: '0.9688rem',
                lineHeight: 1.78,
                fontWeight: 400,
                letterSpacing: '-0.005em'
              }}
            >
              {renderProseLinks(message)}
            </p>
          )}

          {/* Where a looked-up claim came from. Absent whenever nothing was retrieved. */}
          {isArvexis && provenance && (
            <MessageProvenance provenance={provenance} lang={lang} />
          )}
          
          {/* Message Actions — Always visible but subtle, more prominent on interaction
              DISCOVERABILITY: Users should immediately understand they can interact */}
          {message && (
            <MessageActions 
              message={message}
              messageId={messageId}
              messageTimestamp={messageTimestamp}
              sessionId={sessionId}
              onListen={onListen}
              isSpeaking={isSpeaking}
              isPreparing={isPreparing}
              lang={lang}
              isHighlighted={isInteracting || isSpeaking || isPreparing}
              isLatest={isLatest}
            />
          )}
        </div>
      ) : (
        // USER MESSAGE — Soft, understated card with Edit/Copy
        <div className="flex justify-end">
          <div className="max-w-[85%]">
            {/* The message card */}
            <div
              className="px-4 py-3"
              style={{
                background: 'rgba(255, 255, 255, 0.055)',
                border: '1px solid rgba(244, 239, 231, 0.11)',
                borderRadius: '16px',
                backdropFilter: 'blur(14px)'
              }}
            >
              {/* Shared Content Preview (for user messages with attachments) */}
              {sharedContent && (
                <SharedContentPreview content={sharedContent} />
              )}
              
              {isEditing ? (
                // Edit mode
                <div>
                  <textarea
                    ref={editInputRef}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={handleEditKeyDown}
                    className="w-full bg-transparent resize-none focus:outline-none"
                    style={{ 
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                      color: 'rgba(255, 255, 255, 0.85)',
                      fontSize: '0.875rem',
                      lineHeight: 1.6
                    }}
                    rows={Math.max(2, editText.split('\n').length)}
                  />
                  <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-white/5">
                    <button
                      onClick={handleCancelEdit}
                      className="px-3 py-1 text-xs text-white/40 hover:text-white/60 transition-colors"
                    >
                      {lang === 'es' ? 'Cancelar' : 'Cancel'}
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="px-3 py-1 text-xs bg-white/10 hover:bg-white/15 text-white/80 rounded-lg transition-colors"
                    >
                      {lang === 'es' ? 'Guardar' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                // Display mode
                message && (
                  <p
                    className="leading-[1.6] whitespace-pre-wrap"
                    style={{ 
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                      color: 'rgba(255, 255, 255, 0.75)',
                      fontSize: '0.875rem',
                      fontWeight: 400
                    }}
                  >
                    {message}
                  </p>
                )
              )}
            </div>
            
            {/* User Message Actions — Edit & Copy */}
            {!isEditing && message && (
              <motion.div 
                className="flex justify-end items-center gap-0.5 mt-1.5 mr-1"
                initial={{ opacity: 0.15 }}
                animate={{ opacity: isInteracting ? 0.6 : 0.15 }}
                whileHover={{ opacity: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                {/* Edit */}
                <button
                  onClick={handleStartEdit}
                  className="p-1.5 rounded-lg text-white/60 hover:text-white/90 transition-all duration-200"
                  title={lang === 'es' ? 'Editar' : 'Edit'}
                  data-testid="user-message-edit"
                >
                  <Pencil className="w-3 h-3" />
                </button>
                
                {/* Copy */}
                <button
                  onClick={handleCopyUser}
                  className={`p-1.5 rounded-lg transition-all duration-200 ${
                    copied 
                      ? 'text-emerald-400' 
                      : 'text-white/60 hover:text-white/90'
                  }`}
                  title={copied ? (lang === 'es' ? 'Copiado' : 'Copied') : (lang === 'es' ? 'Copiar' : 'Copy')}
                  data-testid="user-message-copy"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </motion.div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// =============================================================================
// CRISIS RESOURCES — Shown when safety layer detects crisis
// =============================================================================
const CrisisResources = ({ resources, lang }) => {
  if (!resources) return null;
  
  // PASS D — the service returns { message, resources: [{name, number|action, description}],
  // closing }. This block reads that contract, the same one Talk already renders. `hotlines` is
  // kept as a fallback so an older payload shape still displays.
  const lines = resources.resources || resources.hotlines || [];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-4 mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30"
      data-testid="message-safety-notice"
    >
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-medium text-amber-400">
          {lang === 'es' ? 'Recursos de apoyo' : 'Support Resources'}
        </span>
      </div>
      <p className="text-sm text-white/70 mb-3">{resources.message}</p>
      {lines.map((line, idx) => (
        line.number ? (
          <a
            key={idx}
            href={`tel:${line.number}`}
            className="block py-2 text-sm text-amber-400 hover:text-amber-300"
            data-testid={`message-safety-resource-${idx}`}
          >
            {line.number} — {line.name}
          </a>
        ) : (
          <p
            key={idx}
            className="block py-2 text-sm text-amber-400"
            data-testid={`message-safety-resource-${idx}`}
          >
            {line.action} — {line.name}
          </p>
        )
      ))}
      {resources.closing && (
        <p className="mt-2 text-xs text-white/50 font-light">{resources.closing}</p>
      )}
    </motion.div>
  );
};

// =============================================================================
// TURN FAILURE NOTICE — Honest failure, never a fabricated reply
//
// TRUST / NON-FABRICATION:
// When a turn fails (network drop, expired session, backend error), the app must
// NEVER write words into the thread as if Arvexis said them. This notice is
// visibly system-level, non-conversational, and offers a single retry.
// =============================================================================
const FAILURE_LABELS = {
  en: {
    stream: 'Connection interrupted — tap to try again.',
    transcription: "Dictation didn't come through — tap to try again.",
    upload: "That file didn't reach her — please try sharing it again.",
    link: "That link didn't reach her — check the URL and try again.",
    image: "The image didn't come through — tap to try again.",
    listen: "Her voice isn't available right now — nothing was played.",
    // 4.9-B — a real ceiling, said as itself. Used only if the server sent no copy of its own;
    // when it does, its message (which carries the real remaining wait) is shown instead.
    limit: "You've reached the hourly limit for now — it'll open back up shortly.",
    // Capacity, not a fault and not an hourly brake. The server always sends its own copy here
    // (it carries the real reset date); this exists only so the notice is never empty.
    capacity: "You've used this period's messages. Nothing of your conversations or memories has changed."
  },
  es: {
    stream: 'Conexión interrumpida — toca para intentar de nuevo.',
    transcription: 'No se pudo transcribir el dictado — toca para intentar de nuevo.',
    upload: 'Ese archivo no llegó — intenta compartirlo de nuevo.',
    link: 'Ese enlace no llegó — verifica la URL e intenta de nuevo.',
    image: 'La imagen no se creó — toca para intentar de nuevo.',
    limit: 'Has alcanzado el límite por ahora — se abrirá de nuevo en un rato.',
    capacity: 'Has usado los mensajes de este período. Nada de tus conversaciones ni de tus recuerdos ha cambiado.',
    listen: 'Su voz no está disponible ahora — no se reprodujo nada.'
  }
};

const TurnFailureNotice = ({ kind = 'stream', onRetry, isRetrying, lang, message = null }) => {
  const labels = FAILURE_LABELS[lang === 'es' ? 'es' : 'en'];
  const label = message || labels[kind] || labels.stream;

  if (!onRetry) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex justify-center"
        data-testid="turn-failure-notice"
      >
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs text-white/45">
          <AlertCircle className="w-3 h-3" />
          <span>{label}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 flex justify-center"
      data-testid="turn-failure-notice"
    >
      <button
        type="button"
        onClick={onRetry}
        disabled={isRetrying || !onRetry}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs text-white/45 hover:text-white/70 hover:border-white/20 transition-colors disabled:opacity-50"
        data-testid="turn-failure-retry"
      >
        {isRetrying
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <AlertCircle className="w-3 h-3" />}
        <span>{label}</span>
      </button>
    </motion.div>
  );
};


// =============================================================================
// ROOM BOUNDARY PANEL (Founder ruling 2026-06)
//
// The room reached its working limit — that is the whole of what happened, and this panel says
// only that. It is product voice, never Arvexis's: she is not the one declining to continue, she
// has not forgotten anything, nothing was cut off and nobody is being asked to pay to be
// answered. The conversation above stays exactly where it is and stays fully readable, and the
// monthly Message allowance is neither spent nor reset by arriving here.
//
// Both plans meet the same boundary. Only the way forward differs: Free opens an ordinary New
// Conversation, which begins from everything Arvexis already understands about them; Premium and
// Trial step into the next room, carrying the active conversation through the protected handoff.
// =============================================================================
const RoomBoundaryPanel = ({ message, nextRoom, lang, onNewConversation, onNextRoom, onSeePremium }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-3 rounded-2xl px-4 py-3.5"
    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
    data-testid="room-boundary-panel"
    data-next-room={nextRoom ? 'true' : 'false'}
  >
    <div className="flex items-start gap-2.5">
      <DoorOpen className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'rgba(196,176,250,0.75)' }} />
      <p
        className="text-sm leading-relaxed"
        style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.55)' }}
        data-testid="room-boundary-message"
      >
        {message}
      </p>
    </div>
    <div className="flex items-center gap-3 mt-3.5">
      {nextRoom ? (
        <button
          type="button"
          onClick={onNextRoom}
          className="px-4 py-2 rounded-full text-sm border transition-colors"
          style={{ color: 'rgba(196,176,250,0.9)', borderColor: 'rgba(196,176,250,0.25)' }}
          data-testid="room-boundary-next-room"
        >
          {lang === 'es' ? 'Pasar a la siguiente sala' : 'Step into the next room'}
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={onNewConversation}
            className="px-4 py-2 rounded-full text-sm border transition-colors"
            style={{ color: 'rgba(196,176,250,0.9)', borderColor: 'rgba(196,176,250,0.25)' }}
            data-testid="room-boundary-new-conversation"
          >
            {lang === 'es' ? 'Nueva conversación' : 'New conversation'}
          </button>
          <button
            type="button"
            onClick={onSeePremium}
            className="text-xs text-white/40 hover:text-white/65 transition-colors"
            data-testid="room-boundary-see-premium"
          >
            {lang === 'es' ? 'Ver Premium' : 'See Premium'}
          </button>
        </>
      )}
    </div>
  </motion.div>
);


// =============================================================================
// STAGED ATTACHMENT — selection is staging, Send is consent (4.6)
//
// A chosen file sits here, visible and removable, until the member sends it. Nothing has been
// uploaded, nothing has been read, and she has not seen it yet.
// =============================================================================
const STAGED_ICONS = {
  image: Image,
  video: Film,
  audio: Music,
  pdf: FileText,
  text: FileText,
};

// A real first frame from a locally chosen video, drawn once in the browser. The file never
// leaves the device to produce this and nothing is stored: it is the same file the member is
// about to send, shown as itself instead of as an icon. A frame we cannot draw returns null —
// the icon card stands in rather than a fabricated image.
const captureVideoPoster = (file) => new Promise((resolve) => {
  let settled = false;
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  const finish = (poster) => {
    if (settled) return;
    settled = true;
    URL.revokeObjectURL(url);
    resolve(poster);
  };
  const draw = () => {
    try {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) return finish(null);
      const scale = Math.min(1, 720 / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      finish(canvas.toDataURL('image/jpeg', 0.72));
    } catch (e) {
      finish(null);
    }
  };
  video.muted = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.onloadedmetadata = () => {
    try { video.currentTime = Math.min(0.1, (video.duration || 1) / 4); } catch (e) { draw(); }
  };
  video.onseeked = draw;
  video.onerror = () => finish(null);
  setTimeout(() => finish(null), 5000);
  video.src = url;
});

// A locally chosen video's real duration, read from its metadata in the browser. This is the
// number the member can actually predict — bytes are not — so a clip that is too long is refused
// by its length before anything is staged. null means the browser could not tell us, in which
// case the size limit and the server remain the authority.
const readVideoDuration = (file) => new Promise((resolve) => {
  let settled = false;
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  const finish = (seconds) => {
    if (settled) return;
    settled = true;
    URL.revokeObjectURL(url);
    resolve(seconds);
  };
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.onloadedmetadata = () => {
    const d = video.duration;
    finish(Number.isFinite(d) && d > 0 ? d : null);
  };
  video.onerror = () => finish(null);
  setTimeout(() => finish(null), 5000);
  video.src = url;
});

const clockLabel = (seconds) => {
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const StagedAttachment = ({ entry, lang, onRemove }) => {
  if (!entry) return null;
  const Icon = STAGED_ICONS[entry.contentType] || FileText;
  const sizeLabel = entry.size >= 1024 * 1024
    ? `${(entry.size / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(entry.size / 1024))} KB`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: 0.2 }}
      className="mb-2 flex items-center gap-3 px-3 py-2 rounded-2xl"
      style={{
        background: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.08)'
      }}
      data-testid="staged-attachment"
      data-staged-type={entry.contentType}
    >
      {entry.previewUrl && entry.contentType === 'image' ? (
        <img
          src={entry.previewUrl}
          alt=""
          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
        />
      ) : entry.contentType === 'video' && entry.posterUrl ? (
        <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-black/40"
             data-testid="staged-video-preview">
          <img src={entry.posterUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/25">
            <Play className="w-3 h-3 text-white fill-current" />
          </div>
        </div>
      ) : (
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
          <Icon className="w-4 h-4 text-white/40" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="text-sm text-white/70 truncate" data-testid="staged-attachment-name">
          {entry.name}
        </p>
        <p className="text-xs text-white/35">
          {sizeLabel} · {lang === 'es' ? 'listo para enviar' : 'ready to send'}
        </p>
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-full text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors flex-shrink-0"
        aria-label={lang === 'es' ? 'Quitar archivo' : 'Remove attachment'}
        title={lang === 'es' ? 'Quitar' : 'Remove'}
        data-testid="staged-attachment-remove"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};


// Several chosen items wait together. iOS's own Photos picker offers checkmarks and an explicit
// "Add" only when the input accepts more than one file, so accepting a set is what puts that
// confirmation step in the member's hands. Nothing here has been uploaded or read.
const StagedAttachments = ({ items, lang, onRemove }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2 space-y-1.5" data-testid="staged-attachments" data-staged-count={items.length}>
      {items.map((entry, index) => (
        <StagedAttachment
          key={entry.key}
          entry={entry}
          lang={lang}
          onRemove={() => onRemove(index)}
        />
      ))}
    </div>
  );
};


// =============================================================================
// MEDIA CONFIRMATION — the member's own visible authorization
//
// iOS decides when its picker hands files back; an iCloud item can finish downloading and return
// on its own, and a blue checkmark does not say "add this to my message". WebKit's behaviour is
// therefore never treated as the member's consent. Between the picker and the composer sits this
// one step, owned by Arvexis: what was chosen, shown as itself, with Cancel and Add to message.
// Nothing here uploads, analyses, creates a turn or asks her anything. Only Send does that.
// =============================================================================
const MediaConfirmSheet = ({ items, lang, onCancel, onAdd }) => {
  if (!items || items.length === 0) return null;
  const count = items.length;
  const addLabel = count > 1
    ? (lang === 'es' ? `Añadir ${count} elementos` : `Add ${count} items`)
    : (lang === 'es' ? 'Añadir al mensaje' : 'Add to message');

  return (
    <motion.div
      className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{ background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)' }}
      data-testid="media-confirm-sheet"
      data-confirm-count={count}
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.22 }}
        className="w-full max-w-sm rounded-3xl p-5"
        style={{
          background: 'rgba(18, 18, 26, 0.96)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)'
        }}
      >
        <p className="text-base text-white/85 mb-4">
          {lang === 'es' ? '¿Añadir a tu mensaje?' : 'Add to your message?'}
        </p>

        <div className={`grid gap-2 mb-5 ${count > 1 ? 'grid-cols-3' : 'grid-cols-1'}`}>
          {items.map(entry => (
            <div key={entry.key} className="min-w-0" data-testid="media-confirm-item">
              <div className="relative rounded-xl overflow-hidden bg-black/40 aspect-square">
                {entry.contentType === 'image' && entry.previewUrl ? (
                  <img src={entry.previewUrl} alt="" className="w-full h-full object-cover" />
                ) : entry.contentType === 'video' && entry.posterUrl ? (
                  <>
                    <img src={entry.posterUrl} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center border border-white/20">
                        <Play className="w-3.5 h-3.5 text-white fill-current ml-0.5" />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film className="w-6 h-6 text-white/35" />
                  </div>
                )}
              </div>
              <p className="text-[10px] text-white/45 mt-1 truncate">{entry.name}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 rounded-full text-sm text-white/55 hover:text-white/80 border border-white/10 hover:bg-white/[0.04] transition-colors"
            data-testid="media-confirm-cancel"
          >
            {lang === 'es' ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="flex-1 py-3 rounded-full text-sm text-white bg-white/[0.12] hover:bg-white/[0.18] transition-colors"
            data-testid="media-confirm-add"
          >
            {addLabel}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};


// =============================================================================
// MAIN COMPONENT — One Arvexis, One Consciousness
// =============================================================================
const CompanionChat = ({ 
  surface = 'daily_companion',
  dreamId = null,
  lang = 'en',
  onClose,
  initialMessage = null,
  existingSessionId = null,
  // 5.1 device finding. Additive and OFF by default, so every existing entry point — Message
  // History resume, the Settings modal, Dream chat, the /companion chooser — keeps its accepted
  // behaviour byte for byte. Only Home's New Message sets it.
  startNewSession = false,
  autoOpenVoice = false,  // When true, immediately opens Realtime Voice
  // R6 navigation correction: the Message surface hands its back chevron the opener for the
  // Message History it already mounts. Additive and null by default, so Dream chat, the
  // Settings modal and every other entry point keep their accepted behaviour byte for byte.
  registerOpenHistory = null
}) => {
  // Conversation state
  const [sessionId, setSessionId] = useState(existingSessionId);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [crisisResources, setCrisisResources] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Voice Recording Feedback States
  // The user should always understand what Arvexis is doing
  // 🎙 Recording → 📝 Transcribing → Editable transcript → Send
  const [recordingPhase, setRecordingPhase] = useState(null); // 'recording' | 'transcribing' | 'failed' | null
  // Bundle 3B — why a transcription ended without text: 'timeout' (it took too long and was
  // bounded), 'failed' (the request did not succeed), 'empty' (there was no speech in it).
  const [recordingIssue, setRecordingIssue] = useState(null);
  const [isArvexisResponding, setIsArvexisResponding] = useState(false);
  
  // Recording duration tracking for visual feedback
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingIntervalRef = useRef(null);
  
  // Audio level for waveform visualization
  const [audioLevel, setAudioLevel] = useState(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Visible Intelligence — What activity is Arvexis performing?
  // This shows users that Arvexis is actively working, not just waiting
  // Activities: 'thinking' | 'searching' | 'reading' | 'connecting' | 'analyzing_dream' | null
  const [currentActivity, setCurrentActivity] = useState(null);

  // 3.9 — confirms the member-visible record changed, only when a write actually landed.
  const { admission: memoryAdmission, check: checkMemoryAdmission } = useMemoryAdmission();
  const navigate = useNavigate();
  
  // Voice state — NOW USER-CONTROLLED, not automatic
  // Every message has actions: Listen, Copy, Share, Helpful, Not Helpful
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem('arvexis_voice_enabled');
    return saved !== null ? saved === 'true' : true; // Default: true
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingMessageIndex, setSpeakingMessageIndex] = useState(-1);
  // Distinct from speaking: the first chunk is still being generated
  const [preparingMessageIndex, setPreparingMessageIndex] = useState(-1);
  // Failed-turn retry state (honest failure UI — no fabricated replies)
  const [retryingNoticeId, setRetryingNoticeId] = useState(null);
  
  // Live Voice Mode state
  const [showLiveVoice, setShowLiveVoice] = useState(false);
  
  // Realtime Voice Mode state — auto-open if requested
  const [showRealtimeVoice, setShowRealtimeVoice] = useState(autoOpenVoice);
  
  // Shared Presence state — file sharing and conversation history
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMessageHistory, setShowMessageHistory] = useState(false);
  // R6 navigation correction: hand the Message surface's back chevron the opener for the
  // Message History this component already mounts. Nothing about that surface changes.
  useEffect(() => {
    if (!registerOpenHistory) return;
    registerOpenHistory(() => setShowMessageHistory(true));
    return () => registerOpenHistory(null);
  }, [registerOpenHistory]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Phase 7 Unit 2 — Step Into The Next Room. The room changes; the relationship doesn't.
  // `stage` is one of: 'confirm' (continuity is possible) · 'no-carry' (governance will not
  // carry this conversation right now, said plainly) · 'failure' (we could not even ask, so the
  // member has not been moved). Nothing here is gated by tier; every member sees the same thing.
  const [nextRoomStage, setNextRoomStage] = useState(null);
  // Bundle 3A — what comes with the member and what stays behind, as plain counts. Files they
  // shared remain readable in the new room by reference; pictures Arvexis made belong to the
  // room she made them in. The member is told both rather than discovering either.
  const [nextRoomCounts, setNextRoomCounts] = useState({ sources: 0, imagesLeft: 0 });
  // Bundle 4A — Carry Preview. These ARE the reconciled turns the next room will receive, exactly
  // as it will receive them; nothing here is a summary of them. `carryExcluded` holds the handles
  // of turns the member chose to leave behind, for this handoff only — it deletes nothing,
  // forgets nothing and changes nothing, in this conversation or in what Arvexis understands.
  const [carryTurns, setCarryTurns] = useState([]);
  const [carryExcluded, setCarryExcluded] = useState([]);
  const [isPreparingNextRoom, setIsPreparingNextRoom] = useState(false);
  // ROOM BOUNDARY (Founder ruling 2026-06) — this room has reached its working limit. Set only
  // from the server's own 409, never guessed here: the server is the authority on the boundary
  // and this state exists so the member is told once, plainly, instead of pressing Send into a
  // refusal. `{ message, nextRoom }` — nextRoom is whether this member's plan can carry the
  // active conversation through. Nothing is cleared, deleted or hidden: the conversation above
  // stays exactly where it is and stays fully readable.
  const [roomBoundary, setRoomBoundary] = useState(null);
  // The conversation a fresh room will continue from, held only until that room's first turn
  // creates it. Never persisted on this device, never sent on an ordinary turn.
  const pendingHandoffRef = useRef(null);
  const pendingExcludeRef = useRef(null);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [sharedContent, setSharedContent] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  // 4.6 — a selected file waits here, visibly, until the member presses Send.
  const [stagedItems, setStagedItems] = useState([]);
  // Chosen photos/videos waiting for the member's own "Add to message". Not staged, not sent.
  const [pendingMedia, setPendingMedia] = useState([]);
  // Local media URLs stay alive for the life of this conversation view: a sent image or video is
  // the member's own media, not a filename. They are released only when the view closes.
  const localMediaUrlsRef = useRef([]);
  // Option A′ — the member's own photos, held by this view only, so an edit they ask for during
  // this sitting can be made from their real picture. Nothing is retained server-side, and this
  // map dies with the view: after that Arvexis truthfully says the source is gone.
  const sourceFilesRef = useRef(new Map());
  useEffect(() => () => {
    localMediaUrlsRef.current.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) {} });
    localMediaUrlsRef.current = [];
    sourceFilesRef.current.clear();
  }, []);
  
  // Refs
  const messagesEndRef = useRef(null);
  // 4.7 Pass B — the conversation surface itself, so the pinch guard can be scoped to it only.
  const conversationRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Bundle 3B — a pending transcription must always be interruptible, and the member's own
  // recording is kept only long enough to offer one retry. Never uploaded again on its own,
  // never persisted, never sent anywhere except as the transcription request they asked for.
  const transcribeAbortRef = useRef(null);
  const lastRecordingRef = useRef(null);
  const discardedTranscriptionRef = useRef(false);
  const audioRef = useRef(null);
  const listenRef = useRef({ token: 0, abort: null });
  const isMountedRef = useRef(true);
  const fileInputRef = useRef(null);      // File picker: documents, PDFs, saved audio
  const imageInputRef = useRef(null);     // Photo picker
  const cameraInputRef = useRef(null);    // Camera capture
  const videoInputRef = useRef(null);     // Video picker

  // One session id for this conversation, minted before ANY turn or share.
  // Previously an upload made before the first message used a throwaway `temp-` id while the
  // first message minted a different one, so the shared content silently detached from the
  // conversation. Same single session store — this just makes sure everyone uses the same key.
  const sessionIdRef = useRef(existingSessionId || null);
  // Whether this session exists server-side yet (a session id alone doesn't mean it does)
  const sessionPersistedRef = useRef(!!existingSessionId);

  const applySessionId = (id) => {
    sessionIdRef.current = id || null;
    setSessionId(id || null);
    // A different room is a different boundary. Cleared on every room change — New Conversation,
    // a step into the next room, or opening another conversation from Message History.
    setRoomBoundary(null);
  };

  // ===========================================================================================
  // THREAD-SCOPED GENERATION OWNERSHIP
  //
  // A generation belongs to the conversation it was started in. The underlying request survives
  // navigation — nothing here cancels it, and nothing here should — but its STATE must not. The
  // indicator, the composer gate and the growing reply all belong to one thread, and until now
  // they were single values shared by whichever thread happened to be visible, which is how
  // Thread A's "Working it through..." appeared in a fresh Thread B.
  //
  // This ref is the truth; the three existing state values are a projection of it for whichever
  // thread is on screen, so every render site and every gate keeps working unchanged.
  // Shape: { [sessionId]: { bubbleId, turnId, content, generating, responding, activity,
  //                         finished } }
  // ===========================================================================================
  const genByThreadRef = useRef({});

  const ownerIsVisible = (owningSessionId) => sessionIdRef.current === owningSessionId;

  /** Record generation truth for one thread, and show it only if that thread is on screen. */
  const setGen = (owningSessionId, patch) => {
    if (!owningSessionId) return;
    const prev = genByThreadRef.current[owningSessionId] || {};
    const next = { ...prev, ...patch };
    genByThreadRef.current[owningSessionId] = next;
    if (!ownerIsVisible(owningSessionId)) return;
    if ('generating' in patch) setIsLoading(!!next.generating);
    if ('responding' in patch) setIsArvexisResponding(!!next.responding);
    if ('activity' in patch) setCurrentActivity(next.activity ?? null);
  };

  /** Project the visible thread's own generation state onto the shared indicators. */
  const showGenFor = (visibleSessionId) => {
    const rec = (visibleSessionId && genByThreadRef.current[visibleSessionId]) || {};
    setIsLoading(!!rec.generating);
    setIsArvexisResponding(!!rec.responding);
    setCurrentActivity(rec.activity ?? null);
  };

  /**
   * Re-attach a thread's own in-flight or just-finished reply to a freshly loaded message list.
   *
   * Both loaders replace `messages` wholesale with the server's snapshot. That snapshot cannot
   * contain a reply that has not been persisted yet, and the previous code also minted new ids
   * for every loaded message — so a stream still running against this thread had nothing left to
   * write into and its reply silently never appeared. Re-attaching the bubble under its original
   * id restores the target, and the running stream resumes rendering into the reopened thread.
   *
   * `finished` covers the other case: the reply completed while the member was elsewhere and the
   * snapshot was read before it was written. It is appended once and then forgotten, and only if
   * the snapshot does not already carry it, so a reply can never be shown twice.
   */
  const reattachOwnGeneration = (visibleSessionId, loadedMessages) => {
    const rec = visibleSessionId && genByThreadRef.current[visibleSessionId];
    if (!rec) return loadedMessages;
    const already = loadedMessages.some(
      m => m.role === 'arvexis' && rec.content && m.content === rec.content
    );
    if (rec.generating) {
      if (already) return loadedMessages;
      return [...loadedMessages, {
        id: rec.bubbleId,
        role: 'arvexis',
        content: rec.content || '',
        isStreaming: true,
        timestamp: Date.now()
      }];
    }
    if (rec.finished) {
      delete genByThreadRef.current[visibleSessionId];
      if (already || !rec.content) return loadedMessages;
      return [...loadedMessages, {
        id: rec.bubbleId,
        role: 'arvexis',
        content: rec.content,
        isStreaming: false,
        timestamp: Date.now()
      }];
    }
    return loadedMessages;
  };

  const ensureSessionId = () => {
    if (!sessionIdRef.current) {
      applySessionId(`session-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`);
    }
    return sessionIdRef.current;
  };

  // Real upload limits from the backend — so members are told the truth before they wait.
  const [uploadLimits, setUploadLimits] = useState(null);

  // 4.5 device correction: the stable gesture boundary is the whole Message shell — header,
  // conversation, whitespace, composer and controls — not the message list alone.
  useShellGestureGuard();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/shared-content/limits`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setUploadLimits(data);
      } catch (e) {
        // Non-blocking: without limits we simply let the backend decide.
      }
    })();
    return () => { alive = false; };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => { 
      isMountedRef.current = false;
      // Bundle 3B — a pending dictation request does not follow the member out of Message.
      try { transcribeAbortRef.current?.abort('unmounted'); } catch (e) {}
      transcribeAbortRef.current = null;
      lastRecordingRef.current = null;
      // FOUNDER RULING (2026-06, restored 2026-10-08) — READ ALOUD KEEPS PLAYING WHEN THE MEMBER
      // MOVES AROUND ARVEXIS.
      //
      // Leaving the conversation cancels nothing. The player and the request that feeds it are
      // owned by the playback module, not by this mount, so closing Message, opening History or
      // going Home lets her finish the sentence. This cleanup therefore lets go of the local
      // handle and does NOT pause it — pausing here is exactly what cut her off while the element
      // was the canonical surface.
      //
      // What still stops her: explicit Stop, explicitly playing a different response, starting a
      // dictation, opening Talk, a Dream reflection, turning her voice off, deleting the
      // conversation, and signing out.
      audioRef.current = null;
      // Clean up audio analysis
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {}
      }
      // Clean up any active recording on unmount
      // This ensures microphone is released before navigating to Voice mode
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      if (mediaRecorderRef.current) {
        const stream = mediaRecorderRef.current._stream;
        if (mediaRecorderRef.current.state !== 'inactive') {
          try {
            mediaRecorderRef.current.stop();
          } catch (e) {
            // Ignore errors on cleanup
          }
        }
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        mediaRecorderRef.current = null;
      }
      audioChunksRef.current = [];
    };
  }, []);

  // Persist voice preference
  useEffect(() => {
    localStorage.setItem('arvexis_voice_enabled', voiceEnabled.toString());
  }, [voiceEnabled]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, isSpeaking]);

  // Initialize conversation on mount
  useEffect(() => {
    if (!isInitialized) {
      initializeConversation();
    }
  }, [isInitialized]);

  // =============================================================================
  // ARVEXIS SPEAKS — chunked delivery so playback starts on the first sentence group
  //
  // WHY: the whole reply used to be synthesized as one clip before any sound played
  // (measured: 1.5k chars = 18s, 3.2k chars = 22s) and anything over 4096 characters
  // failed outright with no audio and no explanation. Chunking is a DELIVERY mechanism
  // only — the backend splits at sentence boundaries, preserves the text and its order,
  // and every chunk uses the same engine and voice.
  // =============================================================================
  // SHE MAY ALREADY BE SPEAKING WHEN THIS SCREEN OPENS (Founder-authorized 2026-10-08).
  //
  // Playback outlives this mount, so the indicator has to be told the truth rather than assume
  // silence. The owner remembers a message id, not a position: the same index means a different
  // message in a different thread, and a returning member would have watched the wrong line glow.
  useEffect(() => {
    const playing = currentPlayback();
    if (!playing.playing || playing.surface !== 'message' || !playing.messageKey) return;
    const index = messages.findIndex((m) => m.id === playing.messageKey);
    if (index === -1) return;              // she is speaking a response from another thread
    setIsSpeaking(true);
    setSpeakingMessageIndex(index);
    setPreparingMessageIndex(-1);
  }, [messages]);

  const stopListen = useCallback(() => {
    // One owner. The module holds the player and the in-flight request, so a Stop tapped after
    // the member came back from History reaches the sequence that is actually speaking.
    stopPlayback();
    listenRef.current.abort = null;
    audioRef.current = null;
    setIsSpeaking(false);
    setSpeakingMessageIndex(-1);
    setPreparingMessageIndex(-1);
  }, []);

  const speakText = useCallback(async (text, messageIndex, messageKey) => {
    if (!voiceEnabled || !isMountedRef.current) return;

    // A second tap cancels instead of stacking another synthesis
    if (speakingMessageIndex === messageIndex || preparingMessageIndex === messageIndex) {
      reportListenEvent('cancelled', 'user_tap', { message_index: messageIndex });
      stopListen();
      return;
    }

    // Unlock audio inside the tap itself (iOS Safari). One persistent media element plays the
    // whole sequence — canonical since the Founder's physical A/B (2026-10-08): audible with the
    // hardware Silent switch ON, and clean over car Bluetooth, which Web Audio was not. No
    // AudioContext is created here, because creating one is what puts the page on the ambient
    // session the Silent switch mutes.
    const player = new Audio(SILENT_WAV);
    player.volume = ARVEXIS_VOICE.volume;
    player.preload = 'auto';
    player.play().catch(() => {});
    audioRef.current = player;

    const controller = new AbortController();
    listenRef.current.abort = controller;
    // Claiming the session is what stops whatever was speaking — another response, another
    // thread, a Dream reflection. Not the mount, and not a per-mount token: whether this sequence
    // is still the current one is decided by the one owner. Leaving Message does not end it; an
    // explicit stop, or an explicit play of something else, does.
    const myGeneration = claimSpokenSession({
      player, messageKey: messageKey ?? null, surface: 'message', abort: controller
    });
    const alive = () => playbackGeneration() === myGeneration;

    setPreparingMessageIndex(messageIndex);
    const startedAt = Date.now();
    const chunkCache = new Map();
    let planChunks = [];

    const getAudio = (i) => {
      if (!chunkCache.has(i)) {
        const attempt = (async () => {
          const res = await fetchWithAuthRetry(`${API_URL}/api/companion/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            signal: controller.signal,
            body: JSON.stringify({
              text: planChunks[i].text,
              speed: ARVEXIS_VOICE.speed,
              language: lang,
              // Rendering-layer only: don't re-open a thought that is already in progress
              continuation: !!planChunks[i].continuation
            })
          });
          if (!res.ok) {
            const err = new Error(`chunk_http_${res.status}`);
            err.httpStatus = res.status;
            if (res.status === 429) {
              const body = await res.json().catch(() => ({}));
              if (body?.detail?.error_code === 'listen_exhausted') err.listenRefreshesOn = body.detail.refreshes_on;
            }
            throw err;
          }
          const data = await res.json();
          return data.audio;
        })();
        // L1-B — A REJECTED CHUNK IS NOT CACHED.
        // The cache holds the promise, so a rejection used to be cached too: the prefetch below
        // swallows the first failure with .catch(() => {}), and when the loop later awaited the
        // same index it got that same rejected promise back. One transient 503 was therefore
        // permanent for that chunk for the rest of the playback, and no retry could have helped.
        // Dropping the entry lets the loop's own single call ask again — one further attempt, not
        // a retry loop.
        attempt.catch(() => {
          if (chunkCache.get(i) === attempt) chunkCache.delete(i);
        });
        chunkCache.set(i, attempt);
      }
      return chunkCache.get(i);
    };

    try {
      // S1 DEV SPIKE — try one continuous streaming synthesis first. Anything other than a
      // successful start (gate refusal, reply too long, provider refusal, element error before
      // the first audible audio) falls straight through to today's exact chunked path.
      if (MESSAGE_TTS_STREAM_SPIKE) {
        let prep = null;
        let prepStatus = null;
        try {
          const prepRes = await fetchWithAuthRetry(`${API_URL}/api/companion/tts/stream/prepare`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            signal: controller.signal,
            body: JSON.stringify({ text, language: lang })
          });
          prepStatus = prepRes.status;
          if (prepRes.ok) prep = await prepRes.json();
        } catch (error) {
          if (error?.name === 'AbortError' || !alive()) return;
          prepStatus = 'network';
        }
        if (!alive()) return;

        if (prep?.ticket) {
          const streamStartedAt = Date.now();
          // S1 OPTION 2 — DEV DISCRIMINATION TEST (Founder-authorized 2026-06).
          //
          // The prepared audio is fetched to completion and played from memory, so not one byte
          // of it is fetched while she is audible. That is the single property the physically
          // clean arm-A control has and network-coupled element playback does not. Preparation
          // above is untouched: same ticket, same synthesis, same bytes, one request — the
          // buffered route holds the synthesis on the ticket, so nothing is regenerated between
          // preparation and playback.
          let blobUrl = null;
          let fetchedBytes = 0;
          let fetchMs = null;
          try {
            const fetchStartedAt = Date.now();
            const audioRes = await fetchWithAuthRetry(
              `${API_URL}/api/companion/tts/stream?t=${encodeURIComponent(prep.ticket)}`,
              { method: 'GET', credentials: 'include', signal: controller.signal }
            );
            if (!audioRes.ok) {
              const err = new Error(`stream_http_${audioRes.status}`);
              err.httpStatus = audioRes.status;
              throw err;
            }
            const audioBlob = await audioRes.blob();
            fetchMs = Date.now() - fetchStartedAt;
            fetchedBytes = audioBlob.size;
            if (!fetchedBytes) throw new Error('stream_empty_body');
            blobUrl = URL.createObjectURL(audioBlob);
          } catch (error) {
            if (error?.name === 'AbortError' || !alive()) return;
            reportListenEvent('fallback', 'stream_fetch_failed', {
              reason: error?.message?.slice(0, 120),
              http_status: error?.httpStatus || null,
              path: 'stream_s1_blob',
              stream_mode: prep.mode,
              chars: text.length,
              elapsed_ms: Date.now() - startedAt
            });
          }
          if (!alive()) {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            return;
          }

          if (blobUrl) {
            const result = await playStreamedResponse({
              player,
              url: blobUrl,
              isAlive: alive,
              onFirstAudio: () => {
                setPreparingMessageIndex(-1);
                setIsSpeaking(true);
                setSpeakingMessageIndex(messageIndex);
                reportListenEvent('first_audio', 'ok', {
                  ttfa_ms: Date.now() - startedAt,
                  chars: text.length,
                  spoken_chars: prep.chars,
                  engine: prep.engine,
                  mode: 'stream',
                  stream_mode: prep.mode,
                  path: 'stream_s1_blob',
                  fetched_bytes: fetchedBytes,
                  fetch_ms: fetchMs,
                  ready_ms: streamStartedAt - startedAt + (fetchMs || 0)
                });
              }
            });
            URL.revokeObjectURL(blobUrl);
            if (!alive() || result.cancelled) {
              // The stall/interruption record is collected by the element either way, so a
              // stopped playback no longer discards the only evidence of how it behaved.
              reportListenEvent('cancelled', 'stream_blob', {
                path: 'stream_s1_blob',
                stream_mode: prep.mode,
                started: !!result.started,
                ttfa_ms: result.ttfaMs,
                fetched_bytes: fetchedBytes,
                fetch_ms: fetchMs,
                events: result.events,
                elapsed_ms: Date.now() - startedAt
              });
              return;
            }

            if (result.started) {
              if (result.failure) {
                // Failed after she had already started speaking: stop safely, report honestly.
                stopListen();
                setMessages(prev => [...prev, {
                  id: generateMessageId(),
                  role: 'system_error',
                  kind: 'listen',
                  timestamp: Date.now()
                }]);
                reportListenEvent('failed', 'stream_mid_playback', {
                  reason: result.failure.message?.slice(0, 120),
                  path: 'stream_s1_blob',
                  stream_mode: prep.mode,
                  partial: true,
                  chars: text.length,
                  fetched_bytes: fetchedBytes,
                  fetch_ms: fetchMs,
                  events: result.events,
                  elapsed_ms: Date.now() - startedAt
                });
                return;
              }
              reportListenEvent('complete', 'ok', {
                total_ms: Date.now() - startedAt,
                stream_ms: Date.now() - streamStartedAt,
                ttfa_ms: result.ttfaMs,
                chars: text.length,
                spoken_chars: prep.chars,
                mode: 'stream',
                stream_mode: prep.mode,
                completed: !!result.completed,
                events: result.events,
                path: 'stream_s1_blob',
                fetched_bytes: fetchedBytes,
                fetch_ms: fetchMs
              });
              stopListen();
              return;
            }

            reportListenEvent('fallback', 'stream_start_failed', {
              reason: result.failure?.message?.slice(0, 120),
              path: 'stream_s1_blob',
              stream_mode: prep.mode,
              chars: text.length,
              fetched_bytes: fetchedBytes,
              fetch_ms: fetchMs,
              events: result.events,
              elapsed_ms: Date.now() - startedAt
            });
          }
        } else {
          reportListenEvent('fallback', 'stream_unavailable', {
            path: 'stream_s1',
            prepare_status: prepStatus,
            chars: text.length,
            elapsed_ms: Date.now() - startedAt
          });
        }
        // Today's path owns playback from here. The element was left with a stream src.
        try { player.pause(); player.removeAttribute('src'); player.load(); } catch (e) { /* element is still usable */ }
      }

      const planRes = await fetchWithAuthRetry(`${API_URL}/api/companion/tts/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({ text, language: lang })
      });
      if (!alive()) return;
      if (!planRes.ok) {
        const err = new Error(`plan_http_${planRes.status}`);
        err.httpStatus = planRes.status;
        throw err;
      }
      const plan = await planRes.json();
      planChunks = plan.chunks || [];
      if (!planChunks.length) { stopListen(); return; }

      const onFirstAudio = () => {
        setPreparingMessageIndex(-1);
        setIsSpeaking(true);
        setSpeakingMessageIndex(messageIndex);
        reportListenEvent('first_audio', 'ok', {
          ttfa_ms: Date.now() - startedAt,
          chunks: planChunks.length,
          chars: text.length,
          engine: plan.engine,
          mode: 'element'
        });
      };

      const result = await playSequentialChunks({
        player, chunks: planChunks, getAudio, isAlive: alive, onFirstAudio
      });

      if (!alive() || result.cancelled) return;

      // L1-A — the sequence ended on a chunk failure after some of it had already been spoken.
      // What she said is kept; the tail that was already scheduled has finished on its own
      // sentence boundary by the time we get here. The failure is still shown, because Listen
      // never dies silently.
      if (result.failure) {
        if (result.failure.aborted) return;
        stopListen();
        setMessages(prev => [...prev, {
          id: generateMessageId(),
          role: 'system_error',
          kind: 'listen',
          message: listenLimitCopy(result.failure.listenRefreshesOn),
          timestamp: Date.now()
        }]);
        reportListenEvent(
          'failed',
          result.failure.httpStatus ? `http_${result.failure.httpStatus}` : 'error',
          {
            reason: result.failure.message?.slice(0, 120),
            partial: true,
            played_chunks: result.played || 0,
            failed_at_chunk: result.failure.chunkIndex,
            chunks: planChunks.length,
            chars: text.length,
            mode: result.mode,
            elapsed_ms: Date.now() - startedAt
          }
        );
        return;
      }

      reportListenEvent('complete', 'ok', {
        total_ms: Date.now() - startedAt,
        chunks: planChunks.length,
        chars: text.length,
        mode: result.mode,
        underruns: result.underruns || 0
      });
      stopListen();
    } catch (error) {
      if (error?.name === 'AbortError' || !alive()) return;
      console.error('Listen failed:', error);
      stopListen();
      // HONEST FAILURE: Listen never dies silently, and her voice is never substituted
      setMessages(prev => [...prev, {
        id: generateMessageId(),
        role: 'system_error',
        kind: 'listen',
        message: listenLimitCopy(error?.listenRefreshesOn),
        timestamp: Date.now()
      }]);
      reportListenEvent('failed', error?.httpStatus ? `http_${error.httpStatus}` : 'error', {
        reason: error?.message?.slice(0, 120),
        elapsed_ms: Date.now() - startedAt,
        chars: text.length
      });
    }
  }, [voiceEnabled, lang, speakingMessageIndex, preparingMessageIndex, stopListen]);

  // Listen allowance reached: the written reply stays, and the member is told when listening returns.
  const listenLimitCopy = (refreshesOn) => {
    if (!refreshesOn) return undefined;
    const when = new Date(`${refreshesOn}T12:00:00Z`).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { month: 'long', day: 'numeric' });
    return lang === 'es'
      ? `Has usado tu tiempo de escucha de este mes. Vuelve el ${when}; tus mensajes escritos siguen aquí.`
      : `You've used this month's listening time. It refreshes on ${when}; your written replies are all still here.`;
  };

  // Initialize conversation
  // FOUNDER DIRECTION (December 2025):
  // "The chat experience should naturally continue across sessions.
  //  When the user returns later, they should find the conversation
  //  exactly where they left it."
  const initializeConversation = async () => {
    if (existingSessionId) {
      // Legacy path: explicit session to load
      await loadSessionHistory(existingSessionId);
    } else if (!startNewSession) {
      // Auto-load the user's active chat session
      await loadActiveChatSession();
    }
    // 5.1 device finding: when the member asked for a NEW Message, nothing is inherited — no
    // session id, no history, no active-session lookup. The id is issued by this surface's
    // existing lifecycle on their first turn.
    
    setIsInitialized(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Phase 6: the compose control invokes the existing New Conversation behaviour in place —
  // exactly the `startNewSession` path (5.1): nothing is inherited, no session id, no history,
  // no active-session lookup, and the id is issued by this surface's existing lifecycle on the
  // member's first turn. The previous conversation is not deleted, reset, renamed or merged; it
  // stays where it already lives, in Message History.
  const startNewConversation = () => {
    setShowMessageHistory(false);
    setMessages([]);
    applySessionId(null);
    sessionPersistedRef.current = false;
    // THREAD-SCOPED: a fresh conversation opens clean. Whatever is still generating in the
    // thread the member just left keeps its own state and keeps running; it simply stops
    // driving this screen.
    showGenFor(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // =============================================================================
  // PHASE 7 UNIT 2 — STEP INTO THE NEXT ROOM
  //
  // A member-initiated continuity convenience, and nothing more. Message has no "thread full"
  // state and this flow never implies one: the member steps through because they want to carry
  // on somewhere fresh, not because anything is running out.
  //
  // Order matters. We ASK the server what could actually carry BEFORE moving anyone, so nobody
  // is ever landed in a fresh room on a promise of continuity that governance will not honour.
  // If we cannot even ask, the member does not move at all.
  // =============================================================================
  const askForNextRoom = async () => {
    if (!messages.length) return;
    setIsPreparingNextRoom(true);
    try {
      const response = await fetch(`${API_URL}/api/companion/chat/handoff/preflight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionIdRef.current || '' })
      });
      if (!response.ok) throw new Error('preflight failed');
      const data = await response.json();
      // D4 — Step Into the Next Room is a Premium capability. A Free member is shown the
      // upgrade, in the same modal, instead of a Carry Preview for a move that will not happen.
      // Nothing about their memory or continuity changes, and the copy below says so.
      if (data.entitled === false) {
        setNextRoomStage('upgrade');
        return;
      }
      setNextRoomCounts({ sources: data.sources || 0, imagesLeft: data.images_left || 0,
                          sourceNames: data.source_names || [] });
      setCarryTurns(data.turns || []);
      setCarryExcluded([]);
      setNextRoomStage(data.can_carry ? 'confirm' : 'no-carry');
    } catch (error) {
      // We never guess on the member's behalf. They stay exactly where they are.
      console.debug('Next room preflight unavailable:', error);
      setNextRoomStage('failure');
    } finally {
      setIsPreparingNextRoom(false);
    }
  };

  // The step itself. The previous conversation is not deleted, closed, renamed or merged — it
  // stays in Message History exactly as it is. Nothing durable moves or is copied: what Arvexis
  // understands about the member was never held by a conversation in the first place.
  const stepIntoNextRoom = ({ carrying }) => {
    const origin = sessionIdRef.current;
    const willCarry = carrying && origin && sessionPersistedRef.current;
    pendingHandoffRef.current = willCarry ? origin : null;
    // Bundle 4A — only what survived the member's own choices travels, and the coherence rule is
    // applied where it is enforced: on the server, on every read.
    pendingExcludeRef.current = willCarry && carryExcluded.length ? [...carryExcluded] : null;
    setNextRoomStage(null);
    setShowMessageHistory(false);
    applySessionId(null);
    sessionPersistedRef.current = false;
    // A quiet, non-attributed marker. Arvexis is never given words she did not say; her opening
    // line below is her own, generated in her own register over the context that actually came.
    setMessages(pendingHandoffRef.current
      ? [{ id: generateMessageId(), role: 'room_marker', carried: true, timestamp: Date.now() }]
      : []);
    setCarryTurns([]);
    setCarryExcluded([]);
    setTimeout(() => inputRef.current?.focus(), 100);
    if (willCarry) speakArrival(origin, pendingExcludeRef.current);
  };

  // Bundle 4B — EXPRESSIVE ARRIVAL. She says the first thing, in the room they just stepped into,
  // over the context that actually carried. If anything at all goes wrong she simply does not
  // speak first: the member types, exactly as before. Nothing about this becomes memory.
  const speakArrival = async (origin, exclude) => {
    const room = ensureSessionId();
    // THREAD-SCOPED: the arrival line belongs to the room it is spoken into.
    setGen(room, { responding: true });
    try {
      const response = await fetch(`${API_URL}/api/companion/chat/arrival`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: room, handoff_from: origin,
                               handoff_exclude: exclude || undefined })
      });
      if (!response.ok) return;
      const data = await response.json();
      if (!data.spoke || !data.text) return;
      // The room now exists and holds her line, so the reference is recorded with it.
      pendingHandoffRef.current = null;
      pendingExcludeRef.current = null;
      sessionPersistedRef.current = true;
      setMessages(prev => [...prev, {
        id: generateMessageId(), role: 'arvexis', content: data.text, timestamp: Date.now()
      }]);
    } catch (error) {
      console.debug('No arrival line:', error);
    } finally {
      setGen(room, { responding: false });
    }
  };

  // R3: member-accessible deletion of this conversation.
  // The recording goes; the relationship does not. Explicit Forget is separate.
  const deleteThisConversation = async () => {
    // She does not go on reading a conversation the member has just deleted. Read Aloud now
    // survives navigation, so this destructive action has to say so for itself.
    stopListen();
    // A session id can exist locally before any turn reached the server (e.g. a rejected share).
    // There is nothing stored to delete in that case — just clear what's on screen.
    if (!sessionIdRef.current || !sessionPersistedRef.current) {
      setMessages([]);
      applySessionId(null);
      sessionPersistedRef.current = false;
      setShowDeleteConfirm(false);
      return;
    }
    setIsDeletingChat(true);
    try {
      const response = await fetch(`${API_URL}/api/companion/chat/session/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok && response.status !== 404) throw new Error('delete failed');
      // THREAD-SCOPED: a deleted conversation keeps nothing, including anything it was still
      // generating. Its record is dropped so a late reply cannot be re-attached anywhere.
      if (sessionIdRef.current) delete genByThreadRef.current[sessionIdRef.current];
      setMessages([]);
      applySessionId(null);
      sessionPersistedRef.current = false;
      setShowDeleteConfirm(false);
      showGenFor(null);
    } catch (error) {
      console.error('Conversation delete error:', error);
    } finally {
      setIsDeletingChat(false);
    }
  };

  // Load the user's active/last chat session
  const loadActiveChatSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/companion/chat/active-session`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.debug('No active session found, starting fresh');
        return;
      }
      
      const data = await response.json();
      
      if (data.has_conversation && data.messages && data.messages.length > 0) {
        // Set the session ID to continue this conversation
        applySessionId(data.session_id);
        sessionPersistedRef.current = true;
        
        // Convert history to our message format with unique IDs
        const loadedMessages = data.messages.map((msg, idx) => ({
          id: generateMessageId(),
          role: msg.role === 'assistant' ? 'arvexis' : msg.role,
          content: msg.content,
          ...(msg.image_id ? { generatedImage: { id: msg.image_id, url: `/api/images/${msg.image_id}` } } : {}),
          ...(msg.provenance ? { provenance: msg.provenance } : {}),
          ...(msg.shared_type ? { sharedContent: { type: msg.shared_type, name: msg.shared_name || '' } } : {}),
          timestamp: msg.timestamp || Date.now() + idx
        }));
        
        setMessages(reattachOwnGeneration(data.session_id, loadedMessages));
        // THREAD-SCOPED: show this conversation's own generation state.
        showGenFor(data.session_id);
        // Note: No continuation message added — the conversation naturally continues
        // from where it was left off. Arvexis doesn't announce "we're back."
      }
      // If no conversation exists, Arvexis is simply present
      // The conversation begins when the user speaks or types first
      
    } catch (error) {
      console.debug('Active session load error (starting fresh):', error);
      // Gracefully start fresh — this is not an error condition
    }
  };

  // Load session history for resuming (legacy explicit session)
  const loadSessionHistory = async (sessionIdToLoad) => {
    setIsLoading(true);
    try {      const response = await fetch(`${API_URL}/api/companion/sessions/${sessionIdToLoad}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to load session');
      }
      
      const data = await response.json();
      applySessionId(data.session_id);
      sessionPersistedRef.current = true;
      
      // Convert history to our message format with unique IDs
      const loadedMessages = (data.messages || []).map((msg, idx) => ({
        id: generateMessageId(),
        role: msg.role === 'assistant' ? 'arvexis' : msg.role,
        content: msg.content,
        ...(msg.image_id ? { generatedImage: { id: msg.image_id, url: `/api/images/${msg.image_id}` } } : {}),
        ...(msg.provenance ? { provenance: msg.provenance } : {}),
        ...(msg.shared_type ? { sharedContent: { type: msg.shared_type, name: msg.shared_name || '' } } : {}),
        timestamp: msg.timestamp || Date.now() + idx
      }));
      
      setMessages(reattachOwnGeneration(data.session_id, loadedMessages));
      
    } catch (error) {
      console.error('Load session error:', error);
      // Gentle recovery — just start fresh
    } finally {
      // THREAD-SCOPED: show this conversation's own generation state, not the last one's.
      showGenFor(sessionIdRef.current);
    }
  };

  // =============================================================================
  // CONVERSATION PERSISTENCE — Save messages for continuity across sessions
  // 
  // FOUNDER DIRECTION (December 2025):
  // "The chat experience should naturally continue across sessions.
  //  Closing the application should not end the conversation."
  // =============================================================================
  const saveMessageToSession = async (currentSessionId, role, content, imageId = null) => {
    if (!currentSessionId || (!content && !imageId)) return;
    
    try {
      await fetchWithAuthRetry(`${API_URL}/api/companion/chat/save-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: currentSessionId,
          role,
          content,
          ...(pendingHandoffRef.current ? { handoff_from: pendingHandoffRef.current } : {}),
          ...(pendingExcludeRef.current ? { handoff_exclude: pendingExcludeRef.current } : {}),
          ...(imageId ? { image_id: imageId } : {})
        })
      });
      sessionPersistedRef.current = true;
      // The room now exists, so the reference has been recorded with it. It is never sent again.
      pendingHandoffRef.current = null;
      pendingExcludeRef.current = null;
    } catch (error) {
      // Non-blocking — persistence failure shouldn't interrupt conversation
      console.debug('Message persistence skipped:', error);
    }
  };

  // Option A′ (Founder ruling 2026-09) — edit the member's own photograph from the copy this
  // view still holds. The file is re-sent for this one render; nothing of their original is kept
  // on the server. When this view has released it, the server never asks and Arvexis says plainly
  // that the source is no longer available.
  const editFromSource = async (sharedContentId, instruction, currentSessionId) => {
    const file = sourceFilesRef.current.get(sharedContentId);
    if (!file) {
      pushShareNotice('image_source', lang === 'es'
        ? 'Esa foto ya no está disponible en este dispositivo.'
        : "That photo is no longer available on this device.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', currentSessionId);
      formData.append('shared_content_id', sharedContentId);
      formData.append('instruction', instruction || '');

      const res = await fetch(`${API_URL}/api/images/edit-from-source`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (!res.ok) throw new Error(`edit_from_source_${res.status}`);
      const data = await res.json();
      if (!isMountedRef.current || !data?.image_id) return;
      setMessages(prev => [...prev, {
        id: generateMessageId(),
        role: 'arvexis',
        content: '',
        timestamp: Date.now(),
        generatedImage: { id: data.image_id, url: data.url }
      }]);
    } catch (error) {
      console.error('Photo edit failed:', error);
      // Honest failure — a system notice, never words attributed to Arvexis.
      pushShareNotice('image_source', lang === 'es'
        ? 'No se pudo editar esa foto. Inténtalo de nuevo.'
        : "That edit didn't get made. Try again.");
    } finally {
      if (isMountedRef.current) setCurrentActivity(null);
    }
  };

  // Send message to unified companion chat endpoint
  const sendMessage = async (text, source = 'text', options = {}) => {    if (!text.trim()) return;
    // ROOM BOUNDARY — this room has already told us, from the server, that it is at its working
    // limit. The server remains the authority and would refuse again; not sending simply avoids
    // asking the member to press Send into a refusal they have already been told about.
    if (roomBoundary) return;

    // FOUNDER RULING (2026-06) — SENDING DOES NOT SILENCE HER.
    //
    // `stopListen()` used to run here, so typing a reply while she was still reading the last one
    // cut her off. The member may listen and answer at the same time. Nothing here starts playback
    // either: her next answer is only ever spoken when the member asks for it.

    // For edits and retries, the user message is already in the thread — don't add a new one.
    // `skipUserBubble` is the same situation for a note that arrived with an attachment: the
    // media bubble already carries their words, so a second bubble would invent a second turn.
    const currentSessionId = ensureSessionId();
    // THREAD-SCOPED: the conversation that owns this turn. Every piece of generation state
    // below is recorded against this key, so it can never drive or clear another thread.
    let owningSessionId = currentSessionId;
    let userBubbleId = null;
    if (source !== 'edit' && source !== 'retry' && !options.skipUserBubble) {
      // Add user message immediately. Persistence is the server's job now — it writes both sides
      // of the turn, so nothing depends on this tab surviving or on how a tool turned out.
      const userMessage = { id: generateMessageId(), role: 'user', content: text, source, timestamp: Date.now() };
      userBubbleId = userMessage.id;
      setMessages(prev => [...prev, userMessage]);
      sessionPersistedRef.current = true;
    }
    
    setInputText('');
    // THREAD-SCOPED: this turn belongs to `currentSessionId`. These flags are recorded against
    // that thread and only reach the screen while that thread is the visible one.
    setGen(owningSessionId, { generating: true, responding: true, finished: false });
    
    // Visible Intelligence: Only show when it genuinely adds confidence
    // FOUNDER DIRECTION: "It should only appear when it genuinely adds confidence.
    // Not every response requires an activity indicator. Use them intentionally."
    //
    // 4.3 — ordinary generation gets NO label. It used to guess from keywords ("weather" →
    // "Looking up current information...") and could claim a lookup, a dream reading or a memory
    // search that never ran; that guessing is gone and is not coming back. The 4.3 acceptance
    // delta removed the remaining generic "Reflecting..." too: ordinary response generation is
    // expected conversation, not a capability worth announcing. A state appears only when the
    // backend reports a real non-obvious operation that has actually begun.
    setGen(owningSessionId, { activity: null });
    
    setCrisisResources(null);

    // Generate a message ID for the streaming response
    const arvexisMessageId = generateMessageId();
    // THREAD-SCOPED: the bubble this turn writes into, remembered against its own thread so the
    // reply can be re-attached if the member reopens this conversation while it is still running.
    setGen(owningSessionId, { bubbleId: arvexisMessageId, content: '' });

    // Failure diagnostics for this turn (operational metadata only)
    const turnStartedAt = Date.now();
    let bytesReceived = 0;
    let streamRef = null;
    let failureStage = 'request';
    let fullContent = '';
    let streamError = null;
    let locationRetry = false;
    // A completed turn says so. Without this, a connection that closed having sent her start frame
    // and nothing else was indistinguishable from a finished reply.
    let sawDone = false;
    // 4.9-A — ONE MEMBER ACTION, ONE IDENTITY. Retrying a failed turn reuses this exactly, so a
    // billable creation that already succeeded is reused instead of made a second time. A fresh
    // request gets a fresh identity and generates normally. Carries nothing the member wrote.
    const turnId = options.turnId || generateMessageId();

    try {
      // Use streaming endpoint for progressive response generation
      // "Responses should begin appearing progressively, as they are being generated.
      //  The goal is presence. The user should immediately feel: 'She's responding.'"
      const response = await fetchWithAuthRetry(`${API_URL}/api/companion/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: text,
          session_id: currentSessionId,
          surface: surface,
          language: lang,
          ...(options.sharedType ? { shared_type: options.sharedType } : {}),
          ...(options.sharedName ? { shared_name: options.sharedName } : {}),
          // Option A′ — which of their own photos this view can still re-send right now.
          ...(sourceFilesRef.current.size
            ? { available_source_ids: Array.from(sourceFilesRef.current.keys()) }
            : {}),
          turn_id: turnId,
          // Phase 7 Unit 2 — the conversation this fresh room continues from. Present only on
          // the first turn of a room the member deliberately stepped into, and honoured by the
          // server only when that room is created.
          ...(pendingHandoffRef.current ? { handoff_from: pendingHandoffRef.current } : {}),
          ...(pendingExcludeRef.current ? { handoff_exclude: pendingExcludeRef.current } : {}),
          timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return null; } })(),
          // 4.1 — a consented device fix, only on the retry of a turn the backend told us needs
          // one. Never attached to an ordinary turn, never cached between turns.
          ...(options.deviceLocation ? { location: options.deviceLocation } : {}),
          ...(options.locationPermission ? { location_permission: options.locationPermission } : {})
        })
      });

      if (!response.ok) {
        const httpError = new Error(`Chat request failed: ${response.status}`);
        httpError.httpStatus = response.status;
        // 4.9-B — a real hourly ceiling is not a connection failure. The server already writes
        // member-safe copy carrying the real remaining wait; it was being thrown away and shown
        // as a generic error with a retry button that would fail identically. Read it, say it,
        // and offer no retry while the limit is genuinely still in force.
        if (response.status === 429) {
          try {
            const payload = await response.json();
            httpError.limitMessage = payload?.detail || null;
          } catch (e) { /* no body: the generic notice still stands */ }
          httpError.isLimit = true;
        }
        // Message capacity for this period is spent. This is not a failure and not an hourly
        // brake: only sending a NEW message is blocked. History, Shared Memories, continuity and
        // her personality are untouched, so nothing is cleared and no retry is offered against
        // a limit that is genuinely still in force.
        if (response.status === 402) {
          try {
            const payload = await response.json();
            const detail = payload?.detail;
            // D2 — a capacity refusal may now carry the governed crisis resources, because the
            // capacity counter must not outrank a genuine disclosure. The turn is still refused;
            // the resources simply are not withheld. Ordinary refusals are still a plain string.
            if (detail && typeof detail === 'object') {
              httpError.limitMessage = detail.message || null;
              if (detail.safety?.resources) setCrisisResources(detail.safety.resources);
            } else {
              httpError.limitMessage = detail || null;
            }
          } catch (e) { /* no body: the capacity notice still stands */ }
          httpError.isLimit = true;
          httpError.isCapacity = true;
        }
        // ROOM BOUNDARY — this room has reached its working limit. Not a failure, not the
        // monthly allowance, and not Arvexis refusing: the room itself is full, her words and
        // everything she understands about the member are untouched and stay readable. The
        // member is told once, in the room's own voice, with the one way forward their plan has.
        if (response.status === 409) {
          try {
            const payload = await response.json();
            const detail = payload?.detail;
            if (detail?.reason === 'room_boundary') {
              setRoomBoundary({ message: detail.message || null, nextRoom: !!detail.next_room });
              // The boundary must not withhold a genuine disclosure (the same doctrine the
              // capacity refusal follows). The turn is still refused.
              if (detail.safety?.resources) setCrisisResources(detail.safety.resources);
              httpError.isRoomBoundary = true;
            }
          } catch (e) { /* no body: the boundary panel simply does not appear */ }
          httpError.isLimit = true;
        }
        throw httpError;
      }
      failureStage = 'stream';
      
      // Create empty Arvexis message that will be filled progressively
      setMessages(prev => [...prev, { 
        id: arvexisMessageId, 
        role: 'arvexis', 
        content: '', 
        timestamp: Date.now(),
        isStreaming: true 
      }]);
      // The indicator stays until her first actual word arrives. Clearing it here left an empty
      // bubble during anything slow (image creation, a web lookup) — visible dead air.
      
      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let receivedSessionId = null;
      let lineBuffer = ''; // Buffer for incomplete lines across chunks
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        // Append decoded chunk to buffer
        const chunk = decoder.decode(value, { stream: true });
        bytesReceived += value?.length || 0;
        lineBuffer += chunk;
        
        // Process complete lines only (lines ending with \n)
        const lines = lineBuffer.split('\n');
        // Keep the last element (which may be incomplete) in the buffer
        lineBuffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.session_id && !sessionId) {
                receivedSessionId = data.session_id;
              }
              
              if (data.stream_ref) {
                streamRef = data.stream_ref;
              }
              
              // 4.1 — this turn genuinely needs to know where they are. Ask the device (the
              // browser prompts the first time), then send the SAME turn again with whatever
              // came back. If they say no, she asks for a city instead. Nothing is stored.
              if (data.type === 'location_needed') {
                locationRetry = true;
              }

              // Handle Visible Intelligence activity events from backend
              if (data.type === 'activity' && data.activity) {
                setGen(owningSessionId, { activity: data.activity });
              }

              // PASS D — display parity with Talk. The backend's existing safety assessment
              // reached the client but nothing rendered it here, so the resource block only
              // ever appeared in Talk. It is shown quietly beside the reply and never replaces,
              // interrupts or blocks the conversation. Cleared at the start of every turn.
              if (data.type === 'safety' && data.resources) {
                setCrisisResources(data.resources);
              }
              
              // She looked something up — the sources belong to THIS turn only
              if (data.type === 'provenance') {
                const { type, ...received } = data;
                setMessages(prev => prev.map(msg =>
                  msg.id === arvexisMessageId ? { ...msg, provenance: received } : msg
                ));
              }

              // An image she just made — it belongs above her words, in this same turn
              if (data.type === 'image' && data.image_id) {
                setMessages(prev => prev.map(msg =>
                  msg.id === arvexisMessageId
                    ? { ...msg, generatedImage: { id: data.image_id, url: data.url } }
                    : msg
                ));
              }

              // Option A′ — she's editing THEIR photograph, and only this view still has it.
              // The bytes go up for this one render and are never stored.
              if (data.type === 'image_source_needed' && data.shared_content_id) {
                editFromSource(data.shared_content_id, data.instruction || text, currentSessionId);
              }
              
              if (data.content) {
                if (!fullContent) {
                  setGen(owningSessionId, { responding: false, activity: null });
                }
                fullContent += data.content;
                // THREAD-SCOPED: the reply so far, held against its own thread so reopening this
                // conversation mid-generation restores what she has already said.
                setGen(owningSessionId, { content: fullContent });
                // Update message progressively — this creates presence
                setMessages(prev => prev.map(msg => 
                  msg.id === arvexisMessageId 
                    ? { ...msg, content: fullContent }
                    : msg
                ));
              }
              
              if (data.done && data.type === 'done') {
                sawDone = true;
                // Mark streaming complete. `turnAt` is the timestamp this reply was actually
                // stored under, so a 👍/👎 on it resolves to the stored turn.
                setMessages(prev => prev.map(msg => 
                  msg.id === arvexisMessageId 
                    ? { ...msg, isStreaming: false, turnAt: data.message_timestamp || msg.turnAt }
                    : msg
                ));
                // 3.9 — the continuity write starts after this frame and makes an extraction
                // call, so we wait a short window for a CONFIRMED outcome. Nothing confirmed,
                // nothing shown.
                checkMemoryAdmission({ wait: true });
              }
              
              if (data.error) {
                // Structured backend failure — surface it, don't swallow it.
                // (This must not throw here: the surrounding try/catch is the
                //  per-line JSON parse guard and would silently absorb it.)
                streamError = data;
              }
            } catch (parseError) {
              // Log actual parse errors (not just 'Unexpected end of JSON input')
              // With proper line buffering, parse errors should be rare
              console.warn('SSE parse error:', parseError.message, 'Line:', line.slice(0, 100));
            }
          }
        }
      }
      
      // 4.1 — the backend asked for this turn's location instead of answering. Ask the device
      // now (the browser prompts the first time), drop the empty bubble, and send the same turn
      // again with whatever came back. Granted → she answers for where they actually are.
      // Denied or unavailable → she asks for a city. The fix is used for that one request and
      // never kept.
      if (locationRetry) {
        setMessages(prev => prev.filter(msg => msg.id !== arvexisMessageId));
        const fix = await requestDeviceLocation();
        setGen(owningSessionId, { generating: false, responding: false });
        return sendMessage(text, source, {
          ...options,
          skipUserBubble: true,
          deviceLocation: fix.location,
          locationPermission: fix.permission,
        });
      }

      // A structured error event means the turn failed on the server
      if (streamError) {
        failureStage = streamError.stage || 'stream';
        streamRef = streamError.stream_ref || streamRef;
        const backendError = new Error(streamError.error || 'Stream failed');
        backendError.errorCode = streamError.error_code || 'stream_failed';
        throw backendError;
      }

      // The image was attempted and did not come out — she says so herself now, in the stream,
      // so both sides of the turn stay in the conversation. Nothing to clean up here.

      // Process any remaining content in the buffer
      if (lineBuffer.trim()) {
        if (lineBuffer.startsWith('data: ')) {
          try {
            const data = JSON.parse(lineBuffer.slice(6));
            if (data.content) {
              fullContent += data.content;
              setMessages(prev => prev.map(msg => 
                msg.id === arvexisMessageId 
                  ? { ...msg, content: fullContent }
                  : msg
              ));
            }
            // The closing frame can land here when it arrives without its trailing newline. It is
            // still a completed turn, so it must be read as one.
            if (data.done && data.type === 'done') sawDone = true;
          } catch (e) {
            // Final buffer contained incomplete JSON - log for diagnostic visibility
            console.warn('SSE final buffer incomplete:', lineBuffer.slice(0, 100));
          }
        } else if (lineBuffer.length > 0) {
          // Non-empty buffer that doesn't start with 'data:' - unexpected
          console.warn('SSE unexpected final buffer:', lineBuffer.slice(0, 100));
        }
      }
      
      // Update session ID if received
      if (receivedSessionId && !sessionIdRef.current) {
        // THREAD-SCOPED: carry this turn's record over to the id the server gave the room, so
        // its state is not orphaned under the local id it started with.
        if (genByThreadRef.current[owningSessionId]) {
          genByThreadRef.current[receivedSessionId] = genByThreadRef.current[owningSessionId];
          delete genByThreadRef.current[owningSessionId];
        }
        owningSessionId = receivedSessionId;
        applySessionId(receivedSessionId);
      }
      // Phase 7 Unit 2 — the room exists now and carries its reference, so it is not sent again.
      // If this turn had failed before the room was created, the reference would still be held
      // and a retry would attach it, so a failed first turn cannot strand the member in a cold
      // room: nothing was created, and the original conversation is untouched in Message History.
      pendingHandoffRef.current = null;
      pendingExcludeRef.current = null;

      // A connection that closes having sent her start frame and nothing else did not finish the
      // turn — it lost it. No closing frame, no word of hers, and no error frame to explain it.
      // Read as a completion (what it used to be), it left an empty bubble in the thread, no
      // notice and no way back, while the member's own words were already kept server-side. It is
      // raised here, after this turn's bookkeeping, so the room identity and handoff state are
      // carried over exactly as on any other turn and a retry resumes the same conversation
      // instead of starting a cold one. It then takes the same recoverable path as any other
      // stream failure: the empty bubble is dropped, nothing is written in her name, and the
      // member is offered one retry they choose to take.
      if (!sawDone && !fullContent && !streamError) {
        failureStage = 'stream_closed_empty';
        const emptyStreamClose = new Error('Stream closed before any response');
        emptyStreamClose.errorCode = 'stream_closed_empty';
        throw emptyStreamClose;
      }

    } catch (error) {
      // The member navigated away or reloaded mid-stream. The server has already kept whatever
      // she said, so this is not a failure and there is nobody left to show a notice to.
      if (error?.name === 'AbortError' || !isMountedRef.current) {
        return;
      }
      
      console.error('Chat turn failed:', failureStage, error);

      // ROOM BOUNDARY — the turn was refused before it existed, so nothing of it was written.
      // Their words are handed back to the composer rather than left in the thread as a turn the
      // room does not hold, and the boundary panel below the composer says what happened. No
      // failure notice and no retry: retrying cannot work, and this is not a failure.
      if (error?.isRoomBoundary) {
        setMessages(prev => prev.filter(msg =>
          !(msg.id === arvexisMessageId && !msg.content) && msg.id !== userBubbleId));
        setInputText(text);
        return;
      }

      // TRUST / NON-FABRICATION: never write words into the thread as if Arvexis
      // said them. Drop the empty placeholder turn, keep any real partial text,
      // and show a system-level failure notice with a single retry.
      setMessages(prev => {
        const cleaned = prev.filter(msg => !(msg.id === arvexisMessageId && !msg.content));
        const withoutStreaming = cleaned.map(msg =>
          msg.id === arvexisMessageId ? { ...msg, isStreaming: false } : msg
        );
        return [...withoutStreaming, {
          id: generateMessageId(),
          role: 'system_error',
          kind: error?.isCapacity ? 'capacity' : (error?.isLimit ? 'limit' : 'stream'),
          message: error?.limitMessage || null,
          // 4.9-B — no retry against a limit that is still in force. 4.9-A — the same identity,
          // so a retry recovers the conversation without remaking anything already made.
          retryText: error?.isLimit ? null : text,
          retrySource: source,
          retryTurnId: turnId,
          timestamp: Date.now()
        }];
      });

      // Persist any partial text Arvexis genuinely produced before the failure
      if (fullContent && sessionId) {
        saveMessageToSession(sessionId, 'arvexis', fullContent);
      }

      reportTurnFailure({
        stage: failureStage,
        category: error?.httpStatus ? `http_${error.httpStatus}` : (error?.errorCode || error?.name || 'network'),
        message: error?.message,
        streamRef,
        httpStatus: error?.httpStatus,
        bytesReceived,
        charsReceived: fullContent.length,
        elapsedMs: Date.now() - turnStartedAt,
        source,
        isRetry: source === 'retry'
      });
    } finally {
      // THREAD-SCOPED: this turn is over for ITS OWN thread only. Clearing the shared indicators
      // unconditionally is what used to switch off another conversation's "she's responding"
      // the moment an unrelated turn finished elsewhere.
      setGen(owningSessionId, {
        generating: false, responding: false, activity: null,
        finished: true, content: fullContent
      });
      // Nothing was generated, so there is nothing this thread could need restored.
      if (!fullContent) {
        delete genByThreadRef.current[owningSessionId];
      }
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    // 4.6 — Send is consent. Staged media goes up now, and a note typed alongside it stays part
    // of the SAME member turn: the server defers its own synthetic turn and reply so the
    // conversation records one turn carrying both the attachment and their words.
    if (stagedItems.length > 0) {
      const batch = stagedItems;
      const note = inputText.trim();
      setStagedItems([]);
      setInputText('');
      for (let i = 0; i < batch.length; i++) {
        const isLast = i === batch.length - 1;
        await uploadStagedFile(batch[i], { note: isLast ? note : '' });
      }
      if (note) {
        const last = batch[batch.length - 1];
        await sendMessage(note, 'attachment_note', {
          skipUserBubble: true,
          sharedType: last.contentType,
          sharedName: last.name,
        });
      }
      return;
    }
    sendMessage(inputText, 'text');
  };

  // Retry a failed turn — the user's message stays in the thread, nothing is fabricated
  const handleRetryTurn = async (notice) => {
    if (retryingNoticeId) return;
    setRetryingNoticeId(notice.id);
    setMessages(prev => prev.filter(m => m.id !== notice.id));
    try {
      if (notice.kind === 'transcription' && notice.audioBlob) {
        await transcribeAndSend(notice.audioBlob);
      } else if (notice.retryText) {
        await sendMessage(notice.retryText, 'retry', { turnId: notice.retryTurnId });
      }
    } finally {
      setRetryingNoticeId(null);
    }
  };

  // =============================================================================
  // USER MESSAGE EDITING
  // 
  // FOUNDER DIRECTION (December 2025):
  // "If a user notices a typo, Whisper transcription issue, or simply wants to
  // improve what they wrote, editing should feel effortless. After saving the 
  // edit, Arvexis should naturally continue the conversation from the updated
  // message. There is no need to display warnings. The experience should remain
  // invisible and conversational."
  // =============================================================================
  const handleEditMessage = async (messageId, newContent) => {
    // Find the message index
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return;
    
    // Update the message content
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, content: newContent }
        : msg
    ));
    
    // Remove all messages after this one (the old response is no longer relevant)
    setMessages(prev => prev.slice(0, messageIndex + 1));
    
    // Re-send the conversation with the edited message
    // Arvexis naturally continues from the updated message
    sendMessage(newContent, 'edit');
  };

  // Handle key press (Enter to send, Shift+Enter for newline)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputText, 'text');
    }
  };

  /* =================================================================
     COMPOSER SIZING — the composer stayed 42px tall however long the draft got, so a long
     typed or dictated message had to be reviewed through a one-line window. It now grows with
     the text to a mobile-safe ceiling, then scrolls inside itself; past that, the draft can be
     opened in a larger surface. Same draft throughout — one piece of state, two views.
     ================================================================= */
  const COMPOSER_MAX_HEIGHT = 168; // ~7 lines: comfortable on a phone, still leaves the conversation visible
  const [isComposerExpanded, setIsComposerExpanded] = useState(false);
  const expandedInputRef = useRef(null);
  const selectionRef = useRef(null);

  const autoGrow = (el, max) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  };

  useEffect(() => {
    if (!isComposerExpanded) autoGrow(inputRef.current, COMPOSER_MAX_HEIGHT);
  }, [inputText, isComposerExpanded, isRecording, recordingPhase]);

  // Offer the larger surface only once a draft is actually long enough to need it.
  const draftNeedsRoom = inputText.length > 140 || inputText.split('\n').length > 3;

  const rememberSelection = (el) => {
    if (el) selectionRef.current = [el.selectionStart, el.selectionEnd];
  };

  const restoreSelection = (el) => {
    if (!el) return;
    el.focus();
    const [start, end] = selectionRef.current || [el.value.length, el.value.length];
    try {
      el.setSelectionRange(start, end);
    } catch (err) {
      // Some mobile keyboards reject programmatic selection — focus alone is enough.
    }
  };

  const openExpandedComposer = () => {
    rememberSelection(inputRef.current);
    setIsComposerExpanded(true);
  };

  const collapseComposer = () => {
    rememberSelection(expandedInputRef.current);
    setIsComposerExpanded(false);
  };

  useEffect(() => {
    if (isComposerExpanded) {
      const el = expandedInputRef.current;
      requestAnimationFrame(() => restoreSelection(el));
    } else if (selectionRef.current) {
      requestAnimationFrame(() => restoreSelection(inputRef.current));
    }
  }, [isComposerExpanded]);

  // Voice recording handlers — Using the SAME Whisper pipeline as Dream Recorder
  // ENHANCED UX (December 2025): Recording → Transcribe → Review in composer → Send
  // INLINE COMPOSER (December 2025): Recording state transforms the composer, not a separate overlay
  const startRecording = async () => {
    // Stop any current speech first (and cancel any chunks still generating)
    stopListen();

    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Set up audio analysis for waveform visualization
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;
        
        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);
        
        // Start audio level monitoring
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        const updateLevel = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);
          // Calculate average level (0-1)
          const sum = dataArray.reduce((a, b) => a + b, 0);
          const avg = sum / dataArray.length / 255;
          setAudioLevel(avg);
          animationFrameRef.current = requestAnimationFrame(updateLevel);
        };
        updateLevel();
      } catch (e) {
        console.log('Audio analysis not available:', e);
      }
      
      // Use the same audio format detection as Dream Recorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Store stream reference for cleanup
      mediaRecorderRef.current._stream = stream;

      mediaRecorderRef.current.start(1000); // Collect data every second like Dream Recorder
      setIsRecording(true);
      setRecordingPhase('recording');
      setRecordingDuration(0);
      
      // Start duration counter
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Recording error:', error);
      // Bundle 3B — release the microphone even when setup failed after acquiring it. A failed
      // start must not leave the device listening.
      try {
        stream?.getTracks().forEach(track => track.stop());
      } catch (e) {}
      cleanupAudioAnalysis();
      setIsRecording(false);
      setRecordingPhase(null);
    }
  };

  // Clean up audio analysis
  const cleanupAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {}
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  };

  // Stop recording and transcribe to composer (for review before send)
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Clean up audio analysis
      cleanupAudioAnalysis();
      
      // Clear duration counter
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      const recorder = mediaRecorderRef.current;
      const stream = recorder._stream;
      const mimeType = recorder.mimeType;
      
      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        // Transcribe and populate composer for review
        await transcribeToComposer(audioBlob);
      };
      
      recorder.stop();
      setIsRecording(false);
      setRecordingPhase('transcribing');
    }
  };

  // Cancel recording — discard everything
  const cancelRecording = () => {
    // Clean up audio analysis
    cleanupAudioAnalysis();
    
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    
    if (mediaRecorderRef.current) {
      const stream = mediaRecorderRef.current._stream;
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
    
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordingPhase(null);
    setRecordingDuration(0);
  };

  // Send while recording — finish transcription and send directly
  const sendWhileRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Clean up audio analysis
      cleanupAudioAnalysis();
      
      // Clear duration counter
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      const recorder = mediaRecorderRef.current;
      const stream = recorder._stream;
      const mimeType = recorder.mimeType;
      
      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        // Transcribe and send directly (no review step)
        await transcribeAndSend(audioBlob);
      };
      
      recorder.stop();
      setIsRecording(false);
      setRecordingPhase('transcribing');
    }
  };

  // Transcribe and populate composer for review/editing
  //
  // PHASE 7 BUNDLE 3B — a pending transcription can no longer become a dead end. Founder
  // observed this on iPhone: stop recording, "Transcribing…", and the composer stayed there
  // with no way out but leaving Message entirely. Three things were missing and are now here:
  // a bound (the request cannot outlive TRANSCRIBE_TIMEOUT_MS), a way out (the member can
  // discard a pending transcription at any moment), and a truthful end state with one retry.
  // Nothing about a discarded or failed recording is sent, stored, or reaches relationship
  // memory: the audio never leaves this function except as a transcription request, and a
  // message is only ever authored by the member pressing send.
  const transcribeToComposer = async (audioBlob) => {
    const controller = new AbortController();
    transcribeAbortRef.current = controller;
    lastRecordingRef.current = audioBlob;
    const bound = setTimeout(() => controller.abort('timeout'), TRANSCRIBE_TIMEOUT_MS);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      // Ordinary dictation — explicitly NOT dream conditioning.
      formData.append('context', 'general');

      const response = await fetchWithAuthRetry(`${API_URL}/api/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal
      });

      if (!response.ok) throw new Error('Transcription failed');

      const data = await response.json();

      if (data.text && data.text.trim()) {
        // Populate composer for review — user can edit before sending.
        // FOUNDER RULING (2026-06): a second segment ADDS to the draft. Replacing it deleted what
        // the member had just dictated, so an existing draft is kept and the new words follow it
        // after a single space. An empty composer receives the transcript exactly as before.
        setInputText(prev => {
          const existing = (prev || '').trimEnd();
          const spoken = data.text.trim();
          return existing ? `${existing} ${spoken}` : spoken;
        });
        setRecordingPhase(null);
        setRecordingDuration(0);
        lastRecordingRef.current = null;
      } else {
        // No intelligible speech: nothing is placed in the composer. Never conditioning text,
        // never a silence artefact, never anything the member did not say — but the member is
        // told, instead of being returned to an idle composer with no explanation.
        setRecordingPhase('failed');
        setRecordingIssue('empty');
      }
    } catch (error) {
      if (discardedTranscriptionRef.current) {
        // The member discarded it themselves. That is not a failure and says nothing.
        discardedTranscriptionRef.current = false;
        return;
      }
      console.error('Transcription error:', error);
      setRecordingPhase('failed');
      setRecordingIssue(
        error?.name === 'AbortError' || String(error?.message || '').includes('timeout')
          ? 'timeout' : 'failed');
    } finally {
      clearTimeout(bound);
      transcribeAbortRef.current = null;
    }
  };

  // The way out of a pending transcription, and out of a failed one. Available at every moment
  // either state can exist, so recovery never requires leaving Message.
  const discardTranscription = () => {
    discardedTranscriptionRef.current = true;
    try { transcribeAbortRef.current?.abort('discarded'); } catch (e) {}
    transcribeAbortRef.current = null;
    lastRecordingRef.current = null;
    audioChunksRef.current = [];
    setRecordingPhase(null);
    setRecordingIssue(null);
    setRecordingDuration(0);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  // One retry, of the member's own recording, straight back to the composer — never to send.
  const retryTranscription = () => {
    const blob = lastRecordingRef.current;
    if (!blob) return discardTranscription();
    setRecordingIssue(null);
    setRecordingPhase('transcribing');
    transcribeToComposer(blob);
  };

  // Transcribe and send directly (for "Send while recording")
  const transcribeAndSend = async (audioBlob) => {
    setIsLoading(true);
    const controller = new AbortController();
    transcribeAbortRef.current = controller;
    const bound = setTimeout(() => controller.abort('timeout'), TRANSCRIBE_TIMEOUT_MS);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      // Ordinary dictation — explicitly NOT dream conditioning.
      formData.append('context', 'general');

      const response = await fetchWithAuthRetry(`${API_URL}/api/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal
      });

      if (!response.ok) throw new Error('Transcription failed');
      
      const data = await response.json();
      setRecordingPhase(null);
      setRecordingDuration(0);
      
      if (data.text && data.text.trim()) {
        sendMessage(data.text.trim(), 'voice');
      }
      // No intelligible speech: nothing is sent. A message is never authored on the
      // member's behalf from a recording that held no speech.
    } catch (error) {
      if (discardedTranscriptionRef.current) {
        discardedTranscriptionRef.current = false;
        return;
      }
      console.error('Transcription error:', error);
      setRecordingPhase(null);
      setRecordingDuration(0);
      // HONEST FAILURE: no fabricated Arvexis line. System notice + retry of the same audio.
      setMessages(prev => [...prev, {
        id: generateMessageId(),
        role: 'system_error',
        kind: 'transcription',
        audioBlob,
        timestamp: Date.now()
      }]);
      reportTurnFailure({
        stage: 'transcription',
        category: 'transcription_failed',
        message: error?.message,
        source: 'voice'
      });
    } finally {
      clearTimeout(bound);
      transcribeAbortRef.current = null;
      setIsLoading(false);
    }
  };

  // Toggle voice
  const toggleVoice = () => {
    if (voiceEnabled) {
      stopListen();
    }
    setVoiceEnabled(!voiceEnabled);
  };

  // =============================================================================
  // SHARED PRESENCE — File sharing handlers
  // =============================================================================
  
  // Truthful copy for things the client can tell the member before any upload happens.
  // Mirrors the backend reason codes so the guidance is specific, not a generic refusal.
  const UNSUPPORTED_HINTS = {
    '.doc': {
      en: 'Older Word files (.doc) can\'t be read here. Save it as .docx or PDF and share it again.',
      es: 'Los archivos de Word antiguos (.doc) no se pueden leer aquí. Guárdalo como .docx o PDF y compártelo de nuevo.',
    },
    '.xls': 'spreadsheet', '.xlsx': 'spreadsheet',
    '.ppt': 'slides', '.pptx': 'slides',
    '.pages': 'apple', '.numbers': 'apple', '.key': 'apple',
    '.avi': 'video', '.mkv': 'video', '.wmv': 'video',
  };

  const UNSUPPORTED_GROUPS = {
    spreadsheet: {
      en: 'Spreadsheets can\'t be read here yet. Export the part that matters as PDF or text.',
      es: 'Todavía no se pueden leer hojas de cálculo. Exporta la parte importante como PDF o texto.',
    },
    slides: {
      en: 'Slide decks can\'t be read here yet. Export it as PDF and share it again.',
      es: 'Todavía no se pueden leer presentaciones. Expórtala como PDF y compártela de nuevo.',
    },
    apple: {
      en: 'Pages, Numbers and Keynote files can\'t be read here. Export as PDF or Word and share it again.',
      es: 'Los archivos de Pages, Numbers y Keynote no se pueden leer aquí. Expórtalo como PDF o Word y compártelo de nuevo.',
    },
    video: {
      en: 'That video format can\'t be read here. MP4 and MOV work — most phones record in one of those.',
      es: 'Ese formato de video no se puede leer aquí. Funcionan MP4 y MOV: casi todos los teléfonos graban así.',
    },
  };

  const unsupportedHint = (filename) => {
    const name = (filename || '').toLowerCase();
    for (const [ext, value] of Object.entries(UNSUPPORTED_HINTS)) {
      if (!name.endsWith(ext)) continue;
      const copy = typeof value === 'string' ? UNSUPPORTED_GROUPS[value] : value;
      return lang === 'es' ? copy.es : copy.en;
    }
    return null;
  };

  // Which kind of thing this is, for the staged card and the truthful activity state.
  const getContentType = (mimeType, filename) => {
    const type = mimeType || '';
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/') || /\.(mp4|mov|m4v|mpe?g)$/.test(filename)) return 'video';
    if (type.startsWith('audio/') || /\.(mp3|wav|m4a|ogg|aac|flac)$/.test(filename)) return 'audio';
    if (type === 'application/pdf' || filename.endsWith('.pdf')) return 'pdf';
    return 'text';
  };

  const localShareNotice = (key, params = {}) => {
    const copy = {
      unsupported: {
        en: 'That file type can\'t be read here. PDF, Word (.docx), rich text and plain text files work.',
        es: 'Ese tipo de archivo no se puede leer aquí. Funcionan PDF, Word (.docx), texto enriquecido y archivos de texto.',
      },
      too_large: {
        en: `That file is ${params.sizeMb}MB — the most this supports is ${params.limitMb}MB.`,
        es: `Ese archivo pesa ${params.sizeMb} MB — el máximo admitido aquí es de ${params.limitMb} MB.`,
      },
      too_long: {
        en: `That clip is ${params.length} — the most this supports is ${params.limit}.`,
        es: `Ese clip dura ${params.length} — el máximo admitido aquí es de ${params.limit}.`,
      },
      handoff_failed: {
        en: 'That didn\'t come through from your library. Try "Choose File" instead, or save it to Files first.',
        es: 'Eso no llegó desde tu galería. Prueba con «Elegir archivo» o guárdalo primero en Archivos.',
      },
    }[key];
    return lang === 'es' ? copy.es : copy.en;
  };

  // Which limit actually applies to this file, straight from /api/shared-content/limits.
  const limitMbForFile = (file) => {
    if (!uploadLimits) return null;
    const name = (file.name || '').toLowerCase();
    if (file.type.startsWith('image/')) return uploadLimits.max_image_size_mb;
    if (file.type.startsWith('video/') || /\.(mp4|mov|m4v|mpe?g)$/.test(name)) return uploadLimits.max_video_size_mb;
    if (file.type.startsWith('audio/')) return uploadLimits.max_audio_size_mb;
    if (name.endsWith('.pdf')) return uploadLimits.max_pdf_size_mb;
    if (name.endsWith('.docx')) return uploadLimits.max_file_size_mb;
    return uploadLimits.max_text_size_mb;
  };

  const isSupportedFile = (file) => {
    if (!uploadLimits?.supported_types) return true; // backend remains the authority
    const name = (file.name || '').toLowerCase();
    const all = Object.values(uploadLimits.supported_types).flat();
    return all.some(ext => name.endsWith(ext));
  };

  const pushShareNotice = (kind, message) => {
    setMessages(prev => [...prev, {
      id: generateMessageId(),
      role: 'system_error',
      kind,
      message,
      timestamp: Date.now()
    }]);
  };

  // Video understanding happens after the upload request returns. Follow the real steps the
  // server reports — nothing is displayed that isn't actually running.
  const VIDEO_STAGE_ACTIVITY = {
    received: 'reviewing_video',
    listening: 'listening_video',
    watching: 'watching_video',
    understanding: 'understanding_video',
  };

  const followVideoProcessing = async (contentId, { suppressReply = false } = {}) => {
    const deadline = Date.now() + 4 * 60 * 1000;
    while (Date.now() < deadline && isMountedRef.current) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      let item;
      try {
        const res = await fetch(`${API_URL}/api/shared-content/item/${contentId}`, {
          credentials: 'include'
        });
        if (!res.ok) continue;
        item = await res.json();
      } catch (e) {
        continue;
      }

      if (item.processing_status === 'processing') {
        setCurrentActivity(VIDEO_STAGE_ACTIVITY[item.processing_stage] || 'reviewing_video');
        continue;
      }

      if (item.processing_status === 'completed' && item.natural_response) {
        setSharedContent(prev => [...prev, item]);
        // When the member sent an instruction with the video, her reply belongs to that
        // instruction — not to a second arrival greeting.
        if (!suppressReply) {
          setMessages(prev => [...prev, {
            id: generateMessageId(),
            role: 'arvexis',
            content: item.natural_response,
            timestamp: Date.now()
          }]);
        }
      } else {
        pushShareNotice('upload', item.member_message || null);
      }
      return;
    }
    pushShareNotice('upload', null);
  };

  // Selecting a file is input staging. Sending is consent. (4.6 attachment rule)
  //
  // iOS FIELD FAILURE: a video chosen from the Photo Library showed its blue check and then
  // nothing came back — no card, no error, no Send. Two causes, both closed here:
  //   1. the video input's `accept` listed explicit MIME types; iOS Safari has to transcode a
  //      library video to satisfy that list and, when it can't, hands back an EMPTY FileList with
  //      no error. The image input has always used `image/*` and has always worked. The video
  //      input now uses `video/*` the same way (see the input element).
  //   2. an empty or zero-byte hand-back was indistinguishable from cancelling, so the code
  //      returned silently. It is now told apart and explained.
  const handleFileSelect = async (e) => {
    const input = e.target;
    const selected = Array.from(input.files || []);

    // Cancelling the picker leaves no trace of a selection: nothing staged, nothing shown.
    if (selected.length === 0) {
      input.value = '';
      return;
    }

    // Reset the input so choosing the SAME file again still fires a change event.
    input.value = '';
    setShowShareMenu(false);

    const accepted = [];
    for (const file of selected) {
      // iOS hands back a placeholder when a library video cannot be transcoded. Silence here is
      // what the Founder saw; say it instead.
      if (!file || file.size === 0) {
        pushShareNotice('upload', localShareNotice('handoff_failed'));
        reportTurnFailure({
          stage: 'attachment_selection', category: 'empty_file_handoff',
          message: `name=${file?.name || 'none'} size=${file?.size ?? 'none'}`, source: 'file'
        });
        continue;
      }

      // Tell the member the truth BEFORE anything is staged or uploaded — per file, by name.
      if (!isSupportedFile(file)) {
        pushShareNotice('upload', unsupportedHint(file.name) || localShareNotice('unsupported'));
        continue;
      }
      const limitMb = limitMbForFile(file);
      if (limitMb && file.size > limitMb * 1024 * 1024) {
        pushShareNotice('upload', localShareNotice('too_large', {
          sizeMb: (file.size / (1024 * 1024)).toFixed(1),
          limitMb
        }));
        continue;
      }

      const contentType = getContentType(file.type, file.name.toLowerCase());

      // Length is the limit a member can predict; bytes are not. Whichever ceiling is reached
      // first stops the clip, and the refusal names their real figure next to the maximum.
      if (contentType === 'video') {
        const maxSeconds = uploadLimits?.max_video_duration_seconds;
        const seconds = await readVideoDuration(file);
        if (maxSeconds && seconds && seconds > maxSeconds) {
          pushShareNotice('upload', localShareNotice('too_long', {
            length: clockLabel(seconds),
            limit: clockLabel(maxSeconds)
          }));
          continue;
        }
      }

      accepted.push({
        key: `staged_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        file,
        contentType,
        name: file.name,
        size: file.size,
        previewUrl: (contentType === 'image' || contentType === 'video')
          ? URL.createObjectURL(file)
          : null,
        posterUrl: null,
      });
    }

    if (accepted.length === 0) return;

    // Every local URL is tracked from the moment it exists, so nothing can outlive the view even
    // if the member never reaches Cancel or Add.
    accepted.forEach(entry => {
      if (entry.previewUrl) localMediaUrlsRef.current.push(entry.previewUrl);
    });

    // Photos and videos go to the member's own confirmation step first. Documents and audio come
    // from the Files picker, which already has an explicit Open, so they stage directly.
    const media = accepted.filter(e => e.contentType === 'image' || e.contentType === 'video');
    const direct = accepted.filter(e => e.contentType !== 'image' && e.contentType !== 'video');

    if (direct.length > 0) setStagedItems(prev => [...prev, ...direct]);
    if (media.length > 0) setPendingMedia(prev => [...prev, ...media]);

    // A video's own first frame, drawn locally, replaces the icon as soon as it exists —
    // wherever that item is currently sitting.
    for (const entry of accepted) {
      if (entry.contentType !== 'video') continue;
      captureVideoPoster(entry.file).then(poster => {
        if (!poster) return;
        const withPoster = i => (i.key === entry.key ? { ...i, posterUrl: poster } : i);
        setPendingMedia(prev => prev.map(withPoster));
        setStagedItems(prev => prev.map(withPoster));
      });
    }
  };

  // Cancel discards the pending selection completely — every local URL released, nothing staged,
  // no trace in the conversation. Nothing had been uploaded, so there is nothing to undo server-side.
  const cancelPendingMedia = () => {
    pendingMedia.forEach(entry => {
      if (entry.previewUrl) { try { URL.revokeObjectURL(entry.previewUrl); } catch (e) {} }
    });
    setPendingMedia([]);
  };

  // Add to message is the member's authorization to put it in the composer — still not to send it.
  // Both of these read the pending list and then set state. They must never do it the other way
  // round: React may invoke an updater twice, and a side effect inside one staged the same photo
  // twice — caught in verification.
  const addPendingMedia = () => {
    if (pendingMedia.length === 0) return;
    const confirmed = pendingMedia;
    setPendingMedia([]);
    setStagedItems(prev => [...prev, ...confirmed]);
  };

  const removeStaged = (index) => {
    setStagedItems(prev => {
      const entry = prev[index];
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // The upload itself — it happens on Send, not on selection. `deferMemberTurn` tells the server
  // that the member typed a note, so the turn and the reply belong to their words: one member
  // turn carrying both the media and what they said, never two.
  const uploadStagedFile = async (entry, { note = '' } = {}) => {
    const { file, contentType, previewUrl, posterUrl } = entry;
    const deferMemberTurn = !!note;

    setIsUploading(true);
    // Truthful processing copy — only ever describes what the backend is really doing
    setCurrentActivity(
      contentType === 'image' ? ACTIVITY.LOOKING
        : contentType === 'audio' ? ACTIVITY.LISTENING_RECORDING
          : contentType === 'video' ? ACTIVITY.REVIEWING_VIDEO
            : ACTIVITY.READING
    );

    // Step 1: Add USER message with file preview FIRST
    setMessages(prev => [...prev, { 
      id: generateMessageId(),
      role: 'user', 
      content: note || null, // Their note, in the same turn as the media they attached
      timestamp: Date.now(),
      sharedContent: {
        type: contentType,
        name: file.name,
        size_bytes: file.size,
        // The member's own media, shown as itself. These are local object URLs: they live for
        // this session only and nothing is retained server-side.
        thumbnail: contentType === 'image' ? previewUrl : (contentType === 'video' ? posterUrl : null),
        poster: contentType === 'video' ? posterUrl : null,
        playableUrl: contentType === 'video' ? previewUrl : null,
        uploading: true
      }
    }]);
    
    try {
      // One session for the whole conversation, minted here if this share comes first
      const currentSessionId = ensureSessionId();
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', currentSessionId);
      if (deferMemberTurn) formData.append('defer_member_turn', 'true');
      
      const response = await fetch(`${API_URL}/api/shared-content/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const detail = error.detail;
        const err = new Error(typeof detail === 'string' ? detail : (detail?.code || 'Upload failed'));
        err.memberMessage = typeof detail === 'object' ? detail?.message : null;
        // An allowance that is genuinely spent is not a failure: it is the existing
        // allowance-exhausted experience, which already arrives as the server's own copy on
        // `detail.message`. It must not also be reported as a broken turn.
        err.allowanceExhausted = typeof detail === 'object' && detail?.code === 'video_allowance_exhausted';
        throw err;
      }
      
      const data = await response.json();
      
      // Add to shared content list
      setSharedContent(prev => [...prev, data]);

      // Option A′: keep their own file in this view only, so "edit that photo" can genuinely
      // work from it while the sitting lasts. Never stored server-side, never persisted.
      if (contentType === 'image' && data.id) {
        sourceFilesRef.current.set(data.id, file);
      }
      
      // Update the user message to remove uploading state
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMsgIndex = updated.length - 1;
        if (updated[lastUserMsgIndex]?.sharedContent?.uploading) {
          updated[lastUserMsgIndex] = {
            ...updated[lastUserMsgIndex],
            sharedContent: {
              ...updated[lastUserMsgIndex].sharedContent,
              uploading: false,
              id: data.id
            }
          };
        }
        return updated;
      });
      
      // Step 2: Arvexis only speaks when something readable actually arrived.
      // Otherwise the honest reason appears as a system notice — never as her words.
      if (data.processing_status === 'processing') {
        // Video: understanding runs after the request returns, so follow the real stages.
        await followVideoProcessing(data.id, { suppressReply: deferMemberTurn });
      } else if (data.processing_status === 'completed') {
        // With a note, her answer belongs to the member's own turn, which goes out next —
        // nothing is owed here, and there is no failure to report.
        if (!deferMemberTurn) {
          if (data.natural_response) {
            setMessages(prev => [...prev, {
              id: generateMessageId(),
              role: 'arvexis',
              content: data.natural_response,
              timestamp: Date.now()
            }]);
          } else {
            pushShareNotice('upload', data.member_message || null);
          }
        }
      } else {
        pushShareNotice('upload', data.member_message || null);
      }
      
    } catch (error) {
      console.error('File upload error:', error);
      // HONEST FAILURE: system notice, not words attributed to Arvexis
      pushShareNotice('upload', error?.memberMessage || null);
      if (!error?.allowanceExhausted) {
        reportTurnFailure({ stage: 'shared_content_upload', category: 'upload_failed', message: error?.message, source: 'file' });
      }
    } finally {
      setIsUploading(false);
      setCurrentActivity(null);
    }
  };
  

  // Get opening prompt based on surface
  const getOpeningPrompt = () => {
    if (surface === 'dream_conversation') {
      return lang === 'es' 
        ? '¿Qué te gustaría explorar de tu sueño?' 
        : 'What would you like to explore about your dream?';
    }
    if (surface === 'wellness_conversation') {
      return lang === 'es'
        ? '¿Cómo te sientes hoy?'
        : 'How are you feeling today?';
    }
    if (surface === 'pattern_exploration') {
      return lang === 'es'
        ? '¿Qué patrones has estado notando?'
        : 'What patterns have you been noticing?';
    }
    // daily_companion - open-ended
    return lang === 'es'
      ? '¿Qué tienes en mente?'
      : 'What\'s on your mind?';
  };

  return (
    <div 
      className="flex flex-col h-full utility-surface utility-surface--atmospheric"
      style={{ 
        // The canonical Arvexis atmosphere, the same asset every other elevated surface uses,
        // rather than a bespoke flat gradient. Transparent here so it shows through.
        background: 'transparent',
        // One consistent mobile scale: vertical panning is the only gesture the shell interprets,
        // and an overscroll pull cannot become a page reload. No transform, no fixed width.
        touchAction: 'pan-y',
        overscrollBehavior: 'contain',
      }}
      data-testid="companion-chat"
      data-message-shell="true"
    >
      <style>{`
        /* Message conversation — visual elevation only. Every rule here is presentation:
           no control gains or loses behaviour because of this stylesheet. */
        .msg-head {
          border-bottom: 1px solid rgba(244, 239, 231, 0.07);
          backdrop-filter: blur(14px);
          background: linear-gradient(180deg, rgba(7, 8, 13, 0.62) 0%, rgba(7, 8, 13, 0.18) 100%);
        }
        .msg-head__name {
          font-family: 'Inter', system-ui, sans-serif; font-size: 15px; font-weight: 500;
          letter-spacing: 0.01em; color: rgba(244, 239, 231, 0.92);
        }
        /* Header glyphs: champagne, quiet, generous touch target, no boxes competing with the
           conversation. */
        .msg-icon {
          width: 40px; height: 40px; border-radius: 999px; display: flex; align-items: center;
          justify-content: center; background: transparent; border: none;
          color: rgba(232, 197, 143, 0.82); cursor: pointer;
          transition: color 160ms ease, background-color 160ms ease;
        }
        .msg-icon:hover { color: #E8C58F; background: rgba(232, 197, 143, 0.08); }
        .msg-icon:disabled { color: rgba(244, 239, 231, 0.18); cursor: not-allowed; }
        .msg-icon--muted { color: rgba(244, 239, 231, 0.3); }
        .msg-icon--muted:hover { color: rgba(244, 239, 231, 0.55); background: rgba(255,255,255,0.05); }
        .msg-icon--on { color: #E8C58F; }
        /* Separates the mode controls (Talk, voice) from the conversation-level controls
           (new conversation, history, delete) so each group reads as what it governs. */
        .msg-head__rule {
          width: 1px; height: 20px; flex: 0 0 1px; margin: 0 7px;
          background: rgba(244, 239, 231, 0.12);
        }
        /* Response actions: the same four that already exist, presented as quiet champagne. */
        .msg-act { color: rgba(232, 197, 143, 0.6); }
        .msg-act:hover { color: #E8C58F; background: rgba(232, 197, 143, 0.08); }
        .msg-talk {
          display: flex; align-items: center; gap: 7px; height: 40px; padding: 0 16px;
          border-radius: 999px; cursor: pointer;
          font-family: 'Inter', system-ui, sans-serif; font-size: 14px; font-weight: 500;
          color: rgba(244, 239, 231, 0.95);
          background: linear-gradient(135deg, rgba(126, 96, 196, 0.22) 0%, rgba(74, 86, 170, 0.16) 100%);
          border: 1px solid rgba(150, 126, 222, 0.45);
          transition: border-color 180ms ease, background-color 180ms ease;
        }
        .msg-talk:hover { border-color: rgba(176, 152, 240, 0.7); }
        /* Handheld widths: the same five controls, the same grouping, fitted so the name
           never collides with the control cluster. */
        @media (max-width: 719px) {
          .msg-icon { width: 36px; height: 36px; }
          .msg-talk { height: 36px; padding: 0 13px; }
          .msg-head__rule { margin: 0 3px; height: 18px; }
        }

        /* Composer: a calm pill, a champagne + on the left and a champagne mic on the right. */
        .msg-round {
          width: 44px; height: 44px; flex: 0 0 44px; border-radius: 999px;
          display: flex; align-items: center; justify-content: center; cursor: pointer;
          background: rgba(232, 197, 143, 0.05); border: 1px solid rgba(232, 197, 143, 0.5);
          color: #E8C58F; transition: background-color 170ms ease, border-color 170ms ease;
        }
        .msg-round:hover { background: rgba(232, 197, 143, 0.12); }
        .msg-round:disabled { opacity: 0.4; cursor: not-allowed; }
        .msg-round--on { background: rgba(232, 197, 143, 0.16); }

        /* Attachment tray — a shelf beneath the composer, secondary to it, with a centred
           notch to collapse. The five capabilities are exactly the five that already exist. */
        .msg-tray {
          overflow: hidden; margin-top: 10px; border-radius: 18px;
          background: rgba(255, 255, 255, 0.035);
          border: 1px solid rgba(244, 239, 231, 0.09);
          backdrop-filter: blur(18px);
        }
        .msg-tray__notch {
          display: flex; align-items: center; justify-content: center; width: 100%;
          height: 22px; background: transparent; border: none; cursor: pointer;
          color: rgba(244, 239, 231, 0.34);
        }
        .msg-tray__notch:hover { color: rgba(244, 239, 231, 0.6); }
        .msg-tray__grid {
          display: grid; grid-template-columns: repeat(5, minmax(0, 1fr));
          padding: 2px 4px 14px;
        }
        .msg-tray__item {
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          padding: 8px 2px; background: transparent; border: none; cursor: pointer;
          color: rgba(244, 239, 231, 0.62); min-width: 0;
          font-family: 'Inter', system-ui, sans-serif; font-size: 11.5px; line-height: 1.2;
          transition: color 160ms ease;
        }
        .msg-tray__item:hover { color: #E8C58F; }
        .msg-tray__item span { overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
        @media (min-width: 700px) {
          /* The shelf spans the composer it belongs to, at every width. It is never a
             detached block sitting under one corner of the field. */
          .msg-tray__item { font-size: 12.5px; }
        }
        @media (min-width: 1024px) {
          /* Room for the approved desktop shell on the left. The conversation remains the
             dominant surface; the sidebar sits beside it rather than over it. */
          .msg-screen { left: 205px !important; }
          .msg-head { padding-left: 56px !important; padding-right: 24px; }

          /* ONE CONVERSATION STAGE.
             Arvexis's prose, the member's messages, the composer and the attachment shelf
             all share a single centred column, so a wide display reads as one conversation
             rather than as two distant edges of a room. The atmospheric background stays
             full-bleed behind it; this constrains content, not the surface. */
          .msg-scroll,
          .msg-composer { --msg-stage: 720px; }
          .msg-scroll { padding-left: 24px; padding-right: 24px; }
          .msg-scroll > * { max-width: var(--msg-stage); margin-left: auto; margin-right: auto; }
          .msg-composer { padding-left: 24px; padding-right: 24px; }
          .msg-composer > form,
          .msg-composer > div:not([hidden]) {
            max-width: var(--msg-stage); margin-left: auto; margin-right: auto;
          }
        }
        @media (min-width: 1440px) {
          .msg-scroll, .msg-composer { --msg-stage: 780px; }
        }
        @media (min-width: 1800px) {
          .msg-scroll, .msg-composer { --msg-stage: 840px; }
        }
      `}</style>
      {/* Header — Minimal, unobtrusive */}
      <div 
        className="flex items-center justify-between px-4 py-2 msg-head"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 10px)', 
          paddingLeft: '52px',
        }}
      >
        <div className="flex items-center gap-2.5">
          {/* R6-C: the champagne status dot is removed. It communicated nothing the member
              could act on and read as an unexplained presence/connection indicator. */}
          <span className="msg-head__name">Arvexis</span>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Voice Mode Button */}
          <button
            onClick={() => {
              stopListen();
              setShowRealtimeVoice(true);
            }}
            className="msg-talk"
            title={lang === 'es' ? 'Hablar' : 'Talk'}
            data-testid="realtime-voice-btn"
          >
            <Mic className="w-4 h-4" style={{ color: 'rgba(196, 176, 250, 0.95)' }} />
            <span>{lang === 'es' ? 'Hablar' : 'Talk'}</span>
          </button>
          
          {/* Voice Toggle — Subtle */}
          <button
            onClick={toggleVoice}
            className={voiceEnabled ? 'msg-icon msg-icon--on' : 'msg-icon msg-icon--muted'}
            title={voiceEnabled 
              ? (lang === 'es' ? 'Silenciar' : 'Mute')
              : (lang === 'es' ? 'Activar voz' : 'Enable voice')
            }
            data-testid="voice-toggle-btn"
          >
            {voiceEnabled ? <Volume2 className="w-[18px] h-[18px]" /> : <VolumeX className="w-[18px] h-[18px]" />}
          </button>

          {/* R6-D: conversation-level controls are now one group, separated from the mode
              controls above. New Conversation sits with the conversation it replaces rather
              than floating away from the active Message experience. Behaviour unchanged. */}
          <span className="msg-head__rule" aria-hidden="true" data-testid="msg-head-rule" />

          {/* New Conversation — invokes the existing behaviour only. Nothing else. */}
          <button
            onClick={startNewConversation}
            className="msg-icon"
            title={lang === 'es' ? 'Nueva conversación' : 'New conversation'}
            aria-label={lang === 'es' ? 'Nueva conversación' : 'New conversation'}
            data-testid="new-conversation-btn"
          >
            <PenSquare className="w-[18px] h-[18px]" />
          </button>

          {/* Phase 7 Unit 2 — Step Into The Next Room. A separate intent from New Conversation:
              that one starts clean, this one carries on. Offered only when there is a
              conversation to carry, never automatic, and identical for every member. */}
          <button
            onClick={askForNextRoom}
            disabled={!messages.some((m) => m.role === 'user' || m.role === 'arvexis') || isPreparingNextRoom}
            className="msg-icon"
            title={lang === 'es' ? 'Pasar a la siguiente sala' : 'Step into the next room'}
            aria-label={lang === 'es' ? 'Pasar a la siguiente sala' : 'Step into the next room'}
            data-testid="next-room-btn"
          >
            {isPreparingNextRoom
              ? <Loader2 className="w-[18px] h-[18px] animate-spin" />
              : <DoorOpen className="w-[18px] h-[18px]" />}
          </button>

          {/* R6-C: the Message History glyph is removed from the header. Leaving the
              conversation with the back chevron now opens Message History directly, so a
              second entry point in the header is redundant. The surface, its data and its
              resume behaviour are untouched. */}

          {/* Delete This Conversation — R3. Kept as its own visible control: the reference's
              three-dot menu is not implemented, because four of the five actions it shows do
              not exist in Message today and this pass does not invent behaviour. */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={messages.length === 0}
            className="msg-icon msg-icon--muted"
            title={lang === 'es' ? 'Eliminar esta conversación' : 'Delete this conversation'}
            data-testid="delete-conversation-btn"
          >
            <Trash2 className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>

      {/* Messages Area — Clean, breathing room */}
      {/* 4.7 Pass B: one-finger scrolling only. A two-finger pinch here used to be handled by
          Safari (page zoom) and an overscroll pull could reload the page, which dropped the
          reply in progress. The viewport is untouched, so accessibility enlargement still works. */}
      <div
        ref={conversationRef}
        className="flex-1 overflow-y-auto px-5 py-6 msg-scroll"
        style={{ touchAction: 'pan-y', overscrollBehaviorY: 'contain' }}
        data-testid="conversation-scroll"
      >
        {/* Welcome state — Minimal, inviting */}
        {messages.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="h-full flex flex-col items-center justify-center"
          >
            <div 
              className="w-3 h-3 rounded-full mb-6"
              style={{ 
                background: 'linear-gradient(135deg, #c9a55c 0%, #a08040 100%)',
                boxShadow: '0 0 20px rgba(180, 140, 60, 0.3), 0 0 40px rgba(180, 140, 60, 0.1)'
              }}
            />
            <p 
              className="text-center max-w-xs"
              style={{ 
                fontFamily: '"Inter", sans-serif',
                color: 'rgba(255, 255, 255, 0.35)',
                fontSize: '0.9375rem',
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              {getOpeningPrompt()}
            </p>
          </motion.div>
        )}

        {/* Message list */}
        <AnimatePresence mode="popLayout">
          {messages.map((msg, idx) => {
            // Phase 7 Unit 2 — the arrival marker. Not a message, not attributed to Arvexis,
            // never spoken: a quiet note that the room changed and the thread of the work came
            // with it. Nothing durable moved; it was never held by a conversation.
            if (msg.role === 'room_marker') {
              return (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="my-6 flex flex-col items-center text-center gap-1.5"
                  data-testid="next-room-marker"
                >
                  <div className="flex items-center gap-2">
                    <span className="h-px w-8" style={{ background: 'rgba(196,176,250,0.25)' }} />
                    <DoorOpen className="w-[14px] h-[14px]" style={{ color: 'rgba(196,176,250,0.7)' }} />
                    <span className="h-px w-8" style={{ background: 'rgba(196,176,250,0.25)' }} />
                  </div>
                  <p
                    className="text-sm"
                    style={{ fontFamily: '"Cormorant Garamond", serif', color: 'rgba(255,255,255,0.72)' }}
                  >
                    {lang === 'es'
                      ? 'Nueva sala · continuando desde tu conversación anterior'
                      : 'New room · continuing from your previous conversation'}
                  </p>
                  <p
                    className="text-xs"
                    style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.35)' }}
                    data-testid="next-room-marker-origin"
                  >
                    {lang === 'es'
                      ? 'Tu conversación anterior sigue en el Historial de mensajes.'
                      : 'Your earlier conversation is still in Message History.'}
                  </p>
                </motion.div>
              );
            }

            // Honest failure state — system-level, never presented as Arvexis speaking
            if (msg.role === 'system_error') {
              const canRetry = msg.kind === 'transcription' ? !!msg.audioBlob : !!msg.retryText;
              return (
                <TurnFailureNotice
                  key={msg.id}
                  kind={msg.kind}
                  message={msg.message}
                  lang={lang}
                  isRetrying={retryingNoticeId === msg.id}
                  onRetry={canRetry ? () => handleRetryTurn(msg) : null}
                />
              );
            }
            
            // Find the last Arvexis message index for discoverability
            const lastArvexisIndex = messages.map((m, i) => m.role === 'arvexis' ? i : -1).filter(i => i >= 0).pop();
            const isLatestArvexis = msg.role === 'arvexis' && idx === lastArvexisIndex;
            
            return (
              <MessageBubble
                key={msg.id}
                message={msg.content}
                messageId={msg.id}
                messageTimestamp={msg.turnAt || (typeof msg.timestamp === 'string' ? msg.timestamp : null)}
                sessionId={sessionId}
                isArvexis={msg.role === 'arvexis'}
                isSpeaking={speakingMessageIndex === idx}
                isPreparing={preparingMessageIndex === idx}
                sharedContent={msg.sharedContent}
                generatedImage={msg.generatedImage}
                provenance={msg.provenance}
                onListen={() => speakText(msg.content, idx, msg.id)}
                onEdit={handleEditMessage}
                lang={lang}
                isLatest={isLatestArvexis}
              />
            );
          })}
        </AnimatePresence>

        {/* Crisis resources if shown */}
        <CrisisResources resources={crisisResources} lang={lang} />

        {/* Recording feedback is now handled inline within the dictation composer */}
        
        {/* Visible Intelligence — Shows what Arvexis is doing */}
        <AnimatePresence>
          {isArvexisResponding && currentActivity && (
            <RespondingIndicator lang={lang} activity={currentActivity} />
          )}
        </AnimatePresence>

        {/* Speaking indicator — now only shows during user-initiated playback */}
        <AnimatePresence>
          {isSpeaking && <SpeakingIndicator lang={lang} />}
        </AnimatePresence>

        {/* Visible Intelligence when uploading/processing files */}
        <AnimatePresence>
          {isUploading && currentActivity && (
            <VisibleIntelligence activity={currentActivity} lang={lang} />
          )}
        </AnimatePresence>

        {/* Typing indicator — only when loading and not in other states */}
        {isLoading && !isSpeaking && !recordingPhase && !isArvexisResponding && !isUploading && <TypingIndicator lang={lang} />}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area — Clean, modern */}
      <div 
        className="px-4 py-3 msg-composer"
        style={{ 
          borderTop: '1px solid rgba(244, 239, 231, 0.07)',
          background: 'linear-gradient(0deg, rgba(7, 8, 13, 0.72) 0%, rgba(7, 8, 13, 0.34) 100%)',
          backdropFilter: 'blur(16px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)'
        }}
      >
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.md,.markdown,.rtf,.csv,.json,.xml,.html,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/rtf,text/plain,text/markdown,text/csv,application/json,text/xml,text/html,audio/*,.mp3,.wav,.m4a,.ogg,.webm,.aac,.flac,audio/mpeg,audio/wav,audio/mp4,audio/ogg,audio/webm,audio/aac,audio/flac"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input-document"
        />
        {/* Camera: the device's own capture sheet, nothing custom. What comes back is a still
            image and is read by the same handler as any other photo. */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input-camera"
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*,image/heic,image/heif"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input-image"
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input-video"
        />
        
        <MemoryAdmissionLine
          admission={memoryAdmission}
          lang={lang}
          onOpenMemories={() => navigate('/settings', {
            state: { open: 'shared-memories', focusItemId: memoryAdmission?.itemId || null }
          })}
        />

        <AnimatePresence>
          {pendingMedia.length > 0 && (
            <MediaConfirmSheet
              items={pendingMedia}
              lang={lang}
              onCancel={cancelPendingMedia}
              onAdd={addPendingMedia}
            />
          )}
        </AnimatePresence>

        <StagedAttachments items={stagedItems} lang={lang} onRemove={removeStaged} />

        {/* ROOM BOUNDARY — shown only after the server itself has refused a turn into this room. */}
        <AnimatePresence>
          {roomBoundary && (
            <RoomBoundaryPanel
              message={roomBoundary.message}
              nextRoom={roomBoundary.nextRoom}
              lang={lang}
              onNewConversation={startNewConversation}
              onNextRoom={askForNextRoom}
              onSeePremium={() => navigate('/settings')}
            />
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          {/* Share button ("+") — Hidden during recording */}
          {!isRecording && recordingPhase !== 'transcribing' && (
            <button
              type="button"
              onClick={() => setShowShareMenu(!showShareMenu)}
              disabled={isLoading || isUploading}
              className={showShareMenu ? 'msg-round msg-round--on' : 'msg-round'}
              title={lang === 'es' ? 'Adjuntar' : 'Attach'}
              aria-label={lang === 'es' ? 'Adjuntar' : 'Attach'}
              data-testid="share-btn"
            >
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : showShareMenu ? (
                <X className="w-5 h-5" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
            </button>
          )}
          
          {/* =================================================================
              INLINE COMPOSER — Transforms smoothly between states:
              Idle → Recording → Transcribing → Editable transcript → Send
              
              FOUNDER DIRECTION (December 2025):
              "Keep the interaction anchored inside the existing composer.
               Transform the composer into the recording state rather than
               presenting a separate recording overlay or utility."
              ================================================================= */}
          <div className="flex-1 relative">
            <AnimatePresence mode="wait">
              {/* RECORDING STATE — Waveform replaces text area */}
              {isRecording && (
                <motion.div
                  key="recording"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="w-full px-3 py-2 rounded-2xl flex items-center gap-2"
                  style={{ 
                    minHeight: '42px',
                    background: 'rgba(180, 140, 255, 0.06)',
                    border: '1px solid rgba(180, 140, 255, 0.15)',
                  }}
                  data-testid="composer-recording"
                >
                  {/* Cancel — subtle X at left */}
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="p-1.5 rounded-full text-white/30 hover:text-white/50 hover:bg-white/5 transition-colors flex-shrink-0"
                    title={lang === 'es' ? 'Cancelar' : 'Cancel'}
                    data-testid="recording-cancel-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  
                  {/* Compact waveform fills the space */}
                  <div className="flex-1 flex items-center justify-center">
                    <VoiceWaveform audioLevel={audioLevel} isActive={true} />
                  </div>
                </motion.div>
              )}
              
              {/* TRANSCRIBING STATE — Calm progress indicator */}
              {recordingPhase === 'transcribing' && !isRecording && (
                <motion.div
                  key="transcribing"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="w-full px-4 py-2.5 rounded-2xl flex items-center justify-center gap-3"
                  style={{ 
                    minHeight: '42px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                  data-testid="composer-transcribing"
                >
                  <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
                  <span className="text-white/50 text-sm">
                    {activityLabel(ACTIVITY.TRANSCRIBING, lang)}
                  </span>
                  {/* Bundle 3B — the way out. Present from the first moment this state can
                      exist, so a pending transcription is never a dead end and recovery never
                      requires leaving Message. */}
                  <button
                    onClick={discardTranscription}
                    className="ml-1 p-1 rounded-full text-white/40 hover:text-white/70 transition-colors"
                    title={lang === 'es' ? 'Descartar' : 'Discard'}
                    aria-label={lang === 'es' ? 'Descartar transcripción' : 'Discard transcription'}
                    data-testid="transcribing-cancel-btn"
                  >
                    <X className="w-[15px] h-[15px]" />
                  </button>
                </motion.div>
              )}

              {/* Bundle 3B — TRANSCRIPTION ENDED WITHOUT TEXT. Truthful, bounded, and always
                  escapable. One retry of the member's own recording, straight back to the
                  composer — never to send. Nothing here has been stored or remembered. */}
              {!isRecording && recordingPhase === 'failed' && (
                <motion.div
                  key="transcribe-failed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="w-full px-3.5 py-2.5 rounded-2xl flex items-center justify-between gap-2"
                  style={{
                    minHeight: '42px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                  data-testid="composer-transcribe-failed"
                  data-issue={recordingIssue || 'failed'}
                >
                  <span
                    className="text-[13px] leading-snug text-white/50 flex-1 min-w-0"
                    data-testid="transcribe-failed-message"
                  >
                    {recordingIssue === 'empty'
                      ? (lang === 'es' ? 'No se escuchó nada. Nada se ha enviado.'
                                       : "No speech there. Nothing was sent.")
                      : recordingIssue === 'timeout'
                        ? (lang === 'es' ? 'Tardó demasiado. Nada se ha enviado.'
                                         : 'Took too long. Nothing was sent.')
                        : (lang === 'es' ? 'No se pudo transcribir. Nada se ha enviado.'
                                         : "Couldn't transcribe. Nothing was sent.")}
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    {recordingIssue !== 'empty' && (
                      <button
                        onClick={retryTranscription}
                        className="text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap"
                        style={{ color: 'rgba(196,176,250,0.9)', borderColor: 'rgba(196,176,250,0.25)' }}
                        data-testid="transcribe-retry-btn"
                      >
                        {lang === 'es' ? 'Reintentar' : 'Try again'}
                      </button>
                    )}
                    <button
                      onClick={discardTranscription}
                      className="p-1 rounded-full text-white/40 hover:text-white/70 transition-colors"
                      title={lang === 'es' ? 'Descartar' : 'Discard'}
                      aria-label={lang === 'es' ? 'Descartar grabación' : 'Discard recording'}
                      data-testid="transcribe-discard-btn"
                    >
                      <X className="w-[15px] h-[15px]" />
                    </button>
                  </span>
                </motion.div>
              )}
              
              {/* IDLE/EDITABLE STATE — Normal composer */}
              {!isRecording && recordingPhase !== 'transcribing' && recordingPhase !== 'failed' && (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="relative"
                >
                  <textarea
                    ref={(el) => {
                      inputRef.current = el;
                      // CLUSTER 3.5.4 — grow on MOUNT, not only on change.
                      //
                      // This textarea lives inside <AnimatePresence mode="wait">, so it is
                      // mounted ~200ms after the render that set the text. The height effect
                      // had already run against a null ref by then and nothing re-ran it,
                      // which is why dictated text sat in a one-line box while typed text
                      // grew fine — every keystroke re-runs the effect on a live node.
                      if (el && !isComposerExpanded) autoGrow(el, COMPOSER_MAX_HEIGHT);
                    }}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={lang === 'es' ? 'Mensaje' : 'Message'}
                    className="w-full px-5 py-3 pr-12 resize-none focus:outline-none transition-colors"
                    style={{ 
                      fontFamily: '"Inter", sans-serif',
                      // 16px, not 15px: iOS Safari zooms the page on focus for any field below
                      // 16px, and the viewport meta deliberately keeps pinch-to-zoom available.
                      fontSize: '1rem',
                      minHeight: '44px',
                      maxHeight: `${COMPOSER_MAX_HEIGHT}px`,
                      lineHeight: 1.6,
                      borderRadius: '999px',
                      background: 'rgba(255, 255, 255, 0.045)',
                      border: '1px solid rgba(244, 239, 231, 0.12)',
                      color: 'rgba(244, 239, 231, 0.92)'
                    }}
                    rows={1}
                    disabled={isLoading}
                    data-testid="companion-chat-input"
                  />
                  {draftNeedsRoom && (
                    <button
                      type="button"
                      onClick={openExpandedComposer}
                      className="absolute top-2 right-2.5 p-1.5 rounded-full text-white/25 hover:text-white/60 hover:bg-white/5 transition-colors"
                      title={lang === 'es' ? 'Ampliar' : 'Expand'}
                      aria-label={lang === 'es' ? 'Ampliar el borrador' : 'Expand draft'}
                      data-testid="composer-expand-btn"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* =================================================================
              ACTION BUTTONS — Transform based on composer state
              
              FOUNDER DIRECTION: "Immediately replace the microphone control 
              with a square Stop control in the same action position, so the 
              user intuitively understands that tapping the same area ends recording."
              ================================================================= */}
          
          {/* IDLE STATE: Voice button (mic) — rightmost position.
              FOUNDER RULING (2026-06): a member may dictate several segments into the same draft,
              so an existing draft no longer removes the control that created it. Mic → Stop while
              recording is unchanged, and the staged-attachment condition is deliberately left
              exactly as it was — it was not part of the ruling. */}
          {!isRecording && recordingPhase !== 'transcribing' && stagedItems.length === 0 && (
            <button
              type="button"
              onClick={startRecording}
              disabled={isLoading}
              className="msg-round"
              data-testid="companion-voice-btn"
            >
              <Mic className="w-5 h-5" />
            </button>
          )}
          
          {/* RECORDING STATE: Stop replaces Mic in same position */}
          {isRecording && (
            <button
              type="button"
              onClick={stopRecording}
              className="p-2.5 rounded-full transition-all text-white/70 bg-white/10 hover:bg-white/15"
              title={lang === 'es' ? 'Detener' : 'Stop'}
              data-testid="recording-stop-btn"
            >
              <Square className="w-4 h-4 fill-current" />
            </button>
          )}

          {/* IDLE STATE WITH TEXT, OR A STAGED ATTACHMENT: Send button.
              4.6 — a staged file is enough to send on its own; a note is optional. */}
          {!isRecording && recordingPhase !== 'transcribing' && (inputText.trim() || stagedItems.length > 0) && (
            <button
              type="submit"
              disabled={isLoading || isUploading || (!inputText.trim() && stagedItems.length === 0)}
              className="msg-round"
              data-testid="companion-send-btn"
            >
              {isLoading || isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          )}
          
          {/* TRANSCRIBING STATE: Show loading in send position */}
          {recordingPhase === 'transcribing' && !isRecording && (
            <div className="p-2.5">
              <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
            </div>
          )}
        </form>

        {/* Attachment tray — the approved mobile reference: the composer stays primary and the
            attachment shelf expands and collapses beneath it, with a small centred notch.
            These are EXACTLY the five capabilities Message already supports, calling exactly the
            handlers they called as a popup menu. Nothing was added because an icon looked good. */}
        <AnimatePresence>
          {showShareMenu && !isRecording && recordingPhase !== 'transcribing' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18 }}
              className="msg-tray"
              data-testid="share-menu"
            >
              <button
                type="button"
                onClick={() => setShowShareMenu(false)}
                className="msg-tray__notch"
                title={lang === 'es' ? 'Ocultar adjuntos' : 'Hide attachments'}
                aria-label={lang === 'es' ? 'Ocultar adjuntos' : 'Hide attachments'}
                data-testid="attachment-tray-collapse"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <div className="msg-tray__grid">
                <button
                  type="button"
                  onClick={() => { setShowShareMenu(false); cameraInputRef.current?.click(); }}
                  className="msg-tray__item"
                  data-testid="share-camera-btn"
                >
                  <Camera className="w-[26px] h-[26px]" />
                  <span>{lang === 'es' ? 'Cámara' : 'Camera'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setShowShareMenu(false); imageInputRef.current?.click(); }}
                  className="msg-tray__item"
                  data-testid="share-image-btn"
                >
                  <Image className="w-[26px] h-[26px]" />
                  <span>{lang === 'es' ? 'Foto' : 'Photo'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setShowShareMenu(false); videoInputRef.current?.click(); }}
                  className="msg-tray__item"
                  data-testid="share-video-btn"
                >
                  <Film className="w-[26px] h-[26px]" />
                  <span>{lang === 'es' ? 'Video' : 'Video'}</span>
                </button>
                {/* File covers documents, PDFs and saved audio recordings. A phone keeps voice
                    memos in Files, and every picker routes through the same handler, which reads
                    the file itself — so an audio recording chosen here still reaches audio
                    understanding. That is why the separate Audio entry is gone. */}
                <button
                  type="button"
                  onClick={() => { setShowShareMenu(false); fileInputRef.current?.click(); }}
                  className="msg-tray__item"
                  data-testid="share-document-btn"
                >
                  <FileText className="w-[26px] h-[26px]" />
                  <span>{lang === 'es' ? 'Archivo' : 'File'}</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Live Voice Mode overlay */}
      <AnimatePresence>
        {showLiveVoice && (
          <LiveVoiceMode
            lang={lang}
            sessionId={sessionId}
            onClose={() => setShowLiveVoice(false)}
          />
        )}
      </AnimatePresence>
      
      {/* Realtime Voice Mode overlay (Phase 0 PoC) */}
      <AnimatePresence>
        {showRealtimeVoice && (
          <RealtimeVoiceMode
            lang={lang}
            onClose={() => setShowRealtimeVoice(false)}
          />
        )}
      </AnimatePresence>
      {/* A Talk cut off before the member could decide is offered back here too, since Talk can
          be entered from Message. Independent of the voice connection. */}
      {showRealtimeVoice && <TalkRecoveryOffer surface="talk_from_message" />}
      
      {/* Message History overlay — resumes the chosen session in place */}
      <AnimatePresence>
        {showMessageHistory && (
          <PastReflections
            lang={lang}
            onClose={() => {
              // R6 navigation correction: Message History is now what the conversation's back
              // chevron opens, so its own back continues outward and leaves the Message
              // surface. This keeps Home reachable from Message on a handheld, which the
              // conversation's back chevron used to provide. When Message History was opened
              // without that chevron (no opener registered), it returns to the conversation
              // exactly as it did before.
              setShowMessageHistory(false);
              if (registerOpenHistory) onClose?.();
            }}
            onResumeSession={(sessionIdToResume) => {
              setShowMessageHistory(false);
              if (sessionIdToResume) loadSessionHistory(sessionIdToResume);
            }}
          />
        )}
      </AnimatePresence>
      
      {/* Delete Conversation confirm — R3 */}
      <AnimatePresence>
        {/* Phase 7 Unit 2 — Step Into The Next Room. Three truthful states, one of them a
            plain refusal. Nothing here mentions capacity, limits or anything technical, because
            none of that is what is happening: the member simply wants a fresh room. */}
        {nextRoomStage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={() => setNextRoomStage(null)}
            data-testid="next-room-modal"
            data-stage={nextRoomStage}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-sm bg-gradient-to-br from-[#1A1A24] to-[#0F0F14] rounded-2xl border border-white/10 p-6 max-h-[88vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <DoorOpen className="w-[18px] h-[18px]" style={{ color: 'rgba(196,176,250,0.8)' }} />
                <h3
                  className="text-base"
                  style={{ fontFamily: '"Cormorant Garamond", serif', color: 'rgba(255,255,255,0.9)' }}
                  data-testid="next-room-title"
                >
                  {nextRoomStage === 'confirm'
                    ? (lang === 'es' ? 'Pasar a la siguiente sala' : 'Step into the next room')
                    : nextRoomStage === 'upgrade'
                      ? (lang === 'es' ? 'Pasar a la siguiente sala' : 'Step into the next room')
                      : nextRoomStage === 'no-carry'
                        ? (lang === 'es' ? 'Una sala nueva' : 'A fresh room')
                        : (lang === 'es' ? 'No se pudo preparar' : "Couldn't set that up")}
                </h3>
              </div>
              <p
                className="text-sm leading-relaxed mb-6"
                style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.45)' }}
                data-testid="next-room-explainer"
              >
                {nextRoomStage === 'confirm'
                  ? (lang === 'es'
                      ? 'Se abre una conversación nueva y lo que estáis haciendo ahora continúa allí. Esta conversación sigue en tu Historial de mensajes, tal como está. Lo que Arvexis ha llegado a entender sobre ti no se mueve: ya está contigo, en cualquier sala.'
                      : "A fresh conversation opens and what you're in the middle of continues there. This one stays in your Message History, exactly as it is. What Arvexis has come to understand about you doesn't move — it's already yours, in any room.")
                  : nextRoomStage === 'upgrade'
                    ? (lang === 'es'
                        ? 'Pasar a la siguiente sala forma parte de Premium: abre una conversación nueva y lo que estáis haciendo ahora continúa allí. Tu relación con Arvexis no cambia en nada. Lo que ha llegado a entender sobre ti, tus Recuerdos compartidos y tu Historial de mensajes siguen contigo en cualquier plan — y puedes empezar una conversación nueva cuando quieras.'
                        : "Step into the next room is part of Premium: a fresh conversation opens and what you're in the middle of continues there. Nothing about your relationship with Arvexis changes. What she has come to understand about you, your Shared Memories and your Message History stay with you on any plan — and you can start a new conversation whenever you like.")
                  : nextRoomStage === 'no-carry'
                    ? (lang === 'es'
                        ? 'Esta conversación no puede llevar contexto a una sala nueva ahora mismo. Puedes abrir una sala nueva igualmente, o quedarte aquí y seguir hablando.'
                        : "This conversation can't carry context into a new room right now. You can still open a fresh room, or stay here and keep talking.")
                    : (lang === 'es'
                        ? 'No se pudo preparar la sala nueva ahora mismo. Sigues aquí y nada ha cambiado: esta conversación está intacta.'
                        : "The new room couldn't be set up just now. You're still here and nothing has changed — this conversation is untouched.")}
              </p>
              {nextRoomStage === 'confirm'
                && (nextRoomCounts.sources > 0 || nextRoomCounts.imagesLeft > 0) && (
                <p
                  className="text-xs leading-relaxed mb-6 -mt-3"
                  style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.33)' }}
                  data-testid="next-room-attachments-note"
                >
                  {nextRoomCounts.sources > 0 && (lang === 'es'
                    ? 'Lo que compartiste aquí sigue disponible allí. '
                    : "What you shared here stays readable there. ")}
                  {nextRoomCounts.imagesLeft > 0 && (lang === 'es'
                    ? 'Las imágenes creadas en esta sala se quedan aquí.'
                    : 'Pictures made in this room stay here.')}
                </p>
              )}

              {/* BUNDLE 4A — CARRY PREVIEW. These are the reconciled turns the next room will
                  actually receive, in the words they were said in. Tap one to leave it behind,
                  for this handoff only: nothing is deleted, forgotten, corrected or changed, in
                  this conversation or in what Arvexis understands about them. */}
              {nextRoomStage === 'confirm' && carryTurns.length > 0 && (
                <div className="mb-5 -mt-2" data-testid="carry-preview">
                  <p
                    className="text-[11px] uppercase tracking-[0.18em] mb-2.5"
                    style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(214,193,139,0.55)' }}
                  >
                    {lang === 'es' ? 'Lo que viene contigo' : 'Coming with you'}
                  </p>
                  <div
                    className="rounded-xl overflow-y-auto"
                    style={{ maxHeight: '34vh', background: 'rgba(255,255,255,0.02)',
                             border: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    {carryTurns.map((turn, i) => {
                      const cutAt = carryTurns.reduce(
                        (acc, t, idx) => (carryExcluded.includes(t.key) ? idx : acc), -1);
                      const dropped = i <= cutAt;
                      return (
                        <button
                          key={turn.key}
                          type="button"
                          onClick={() => setCarryExcluded(prev =>
                            prev.includes(turn.key)
                              ? prev.filter(k => k !== turn.key)
                              : [...prev, turn.key])}
                          className="w-full text-left px-3.5 py-2.5 flex gap-2.5 transition-opacity"
                          style={{
                            opacity: dropped ? 0.3 : 1,
                            borderTop: i ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          }}
                          data-testid="carry-turn"
                          data-role={turn.role}
                          data-dropped={dropped ? 'true' : 'false'}
                        >
                          <span
                            className="text-[10px] uppercase tracking-[0.14em] pt-0.5 shrink-0 w-[52px]"
                            style={{ fontFamily: '"Inter", sans-serif',
                                     color: turn.role === 'assistant'
                                       ? 'rgba(214,193,139,0.7)' : 'rgba(255,255,255,0.35)' }}
                          >
                            {turn.role === 'assistant' ? 'Arvexis' : (lang === 'es' ? 'Tu' : 'You')}
                          </span>
                          <span
                            className="text-[13px] leading-snug flex-1 min-w-0"
                            style={{ fontFamily: '"Inter", sans-serif',
                                     color: 'rgba(255,255,255,0.62)',
                                     textDecoration: dropped ? 'line-through' : 'none' }}
                          >
                            {turn.content.length > 180
                              ? `${turn.content.slice(0, 180)}…` : turn.content}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {nextRoomCounts.sources > 0 && (nextRoomCounts.sourceNames || []).length > 0 && (
                    <p
                      className="text-[11px] mt-2.5 truncate"
                      style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.3)' }}
                      data-testid="carry-preview-sources"
                    >
                      {(lang === 'es' ? 'Tambien: ' : 'Also coming: ')}
                      {(nextRoomCounts.sourceNames || []).join(' · ')}
                    </p>
                  )}
                  {carryExcluded.length > 0 && (
                    <p
                      className="text-[11px] mt-2"
                      style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.3)' }}
                      data-testid="carry-preview-coherence"
                    >
                      {lang === 'es'
                        ? 'Lo anterior a eso tampoco viene, para que nada llegue senalando algo que falta.'
                        : "Anything said before that stays too, so nothing arrives pointing at something missing."}
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setNextRoomStage(null)}
                  className="flex-1 py-2.5 rounded-full text-sm text-white/50 hover:text-white/70 border border-white/10 transition-colors"
                  data-testid="next-room-cancel"
                >
                  {nextRoomStage === 'confirm'
                    ? (lang === 'es' ? 'Cancelar' : 'Cancel')
                    : (lang === 'es' ? 'Quedarme aquí' : 'Stay here')}
                </button>
                {nextRoomStage === 'failure' ? (
                  <button
                    onClick={askForNextRoom}
                    disabled={isPreparingNextRoom}
                    className="flex-1 py-2.5 rounded-full text-sm transition-colors disabled:opacity-40 border"
                    style={{ color: 'rgba(196,176,250,0.9)', borderColor: 'rgba(196,176,250,0.25)' }}
                    data-testid="next-room-retry"
                  >
                    {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
                  </button>
                ) : nextRoomStage === 'upgrade' ? (
                  <button
                    onClick={() => { setNextRoomStage(null); navigate('/settings'); }}
                    className="flex-1 py-2.5 rounded-full text-sm transition-colors border"
                    style={{ color: 'rgba(196,176,250,0.9)', borderColor: 'rgba(196,176,250,0.25)' }}
                    data-testid="next-room-upgrade"
                  >
                    {lang === 'es' ? 'Ver Premium' : 'See Premium'}
                  </button>
                ) : (
                  <button
                    onClick={() => stepIntoNextRoom({ carrying: nextRoomStage === 'confirm' })}
                    className="flex-1 py-2.5 rounded-full text-sm transition-colors border"
                    style={{ color: 'rgba(196,176,250,0.9)', borderColor: 'rgba(196,176,250,0.25)' }}
                    data-testid="next-room-confirm"
                  >
                    {nextRoomStage === 'confirm'
                      ? (lang === 'es' ? 'Pasar' : 'Step through')
                      : (lang === 'es' ? 'Abrir una sala nueva' : 'Open a fresh room')}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={() => setShowDeleteConfirm(false)}
            data-testid="delete-conversation-modal"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-sm bg-gradient-to-br from-[#1A1A24] to-[#0F0F14] rounded-2xl border border-white/10 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                className="text-base mb-3"
                style={{ fontFamily: '"Cormorant Garamond", serif', color: 'rgba(255,255,255,0.9)' }}
                data-testid="delete-conversation-title"
              >
                {lang === 'es' ? '¿Eliminar esta conversación?' : 'Delete this conversation?'}
              </h3>
              <p
                className="text-sm leading-relaxed mb-6"
                style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.45)' }}
                data-testid="delete-conversation-explainer"
              >
                {lang === 'es'
                  ? 'Los mensajes se eliminan de forma permanente. Lo que Arvexis ha llegado a entender sobre ti permanece — la conversación se elimina, no la relación. Para que olvide algo, pídeselo directamente.'
                  : 'The messages are permanently deleted. What Arvexis has come to understand about you remains — the conversation is deleted, not the relationship. To have her forget something, ask her directly.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2.5 rounded-full text-sm text-white/50 hover:text-white/70 border border-white/10 transition-colors"
                  data-testid="delete-conversation-cancel"
                >
                  {lang === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={deleteThisConversation}
                  disabled={isDeletingChat}
                  className="flex-1 py-2.5 rounded-full text-sm text-red-300/80 hover:text-red-300 border border-red-400/20 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  data-testid="delete-conversation-confirm"
                >
                  {isDeletingChat
                    ? (lang === 'es' ? 'Eliminando…' : 'Deleting…')
                    : (lang === 'es' ? 'Eliminar' : 'Delete')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* =================================================================
          EXPANDED DRAFT — a temporary reading/editing surface for a long typed or dictated
          message. It edits the SAME `inputText`: no second draft, nothing copied, nothing
          submitted on open or close. Enter inserts a line break here because this is a
          composition surface; sending stays an explicit action.
          ================================================================= */}
      <AnimatePresence>
        {isComposerExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[70] flex flex-col justify-end sm:items-center sm:justify-center px-3 pb-3 sm:p-6"
            style={{ background: 'rgba(6, 8, 14, 0.72)', backdropFilter: 'blur(14px)' }}
            data-testid="composer-expanded-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 18 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="w-full sm:max-w-2xl rounded-3xl overflow-hidden"
              style={{
                background: 'rgba(255, 255, 255, 0.045)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                paddingBottom: 'env(safe-area-inset-bottom)'
              }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <span
                  className="text-white/35 text-xs tracking-wide"
                  style={{ fontFamily: '"Inter", sans-serif' }}
                >
                  {lang === 'es' ? 'Tu mensaje' : 'Your message'}
                </span>
                <button
                  type="button"
                  onClick={collapseComposer}
                  className="p-1.5 rounded-full text-white/35 hover:text-white/70 hover:bg-white/5 transition-colors"
                  title={lang === 'es' ? 'Reducir' : 'Collapse'}
                  aria-label={lang === 'es' ? 'Reducir el borrador' : 'Collapse draft'}
                  data-testid="composer-collapse-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <textarea
                ref={expandedInputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={lang === 'es' ? 'Mensaje' : 'Message'}
                className="w-full px-4 py-4 bg-transparent resize-none focus:outline-none"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  // 16px: same iOS Safari focus-zoom threshold as the primary composer.
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  color: 'rgba(255, 255, 255, 0.88)',
                  height: 'min(52vh, 420px)'
                }}
                disabled={isLoading}
                data-testid="composer-expanded-input"
              />

              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/5">
                <button
                  type="button"
                  onClick={collapseComposer}
                  className="text-xs text-white/35 hover:text-white/60 transition-colors"
                  data-testid="composer-expanded-done-btn"
                >
                  {lang === 'es' ? 'Reducir' : 'Collapse'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsComposerExpanded(false);
                    sendMessage(inputText, 'text');
                  }}
                  disabled={isLoading || !inputText.trim()}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    inputText.trim() && !isLoading
                      ? 'bg-white/10 hover:bg-white/15 text-white/85'
                      : 'bg-white/5 text-white/25 cursor-not-allowed'
                  }`}
                  data-testid="composer-expanded-send-btn"
                >
                  <span className="flex items-center gap-2">
                    <Send className="w-3.5 h-3.5" />
                    {lang === 'es' ? 'Enviar' : 'Send'}
                  </span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default CompanionChat;
